// Sales Request Module — Phase 2
// Visible to rep, supervisor, admin, leadership.
// COST/MARGIN WALL: never display cost, burden, markup, margin, or final rates
// to rep/supervisor. Only target rates (labeled "Target") are shown.
// Leadership gets extra "Queue" tab and "Create Estimate" action.

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { styles, colors, fmtDate, fmtMoney, reqDisplayTitle } from '../components/UI';
import RevisionDrawer from '../components/RevisionDrawer';
import BlurDateInput from '../components/BlurDateInput';
import DropZone from '../components/DropZone';
import { fileDragProps } from '../components/fileDrag';
import MsaSheetEditor from '../components/MsaSheetEditor';
import RequestGuide from '../components/RequestGuide';
import { AmContactLine } from '../components/MyPhone';
import { WORK_DAYS_OPTIONS, phaseHoursPreview, dayCountISO, endFromDays, peakHeadcount, positionsFor } from '../components/laborHours';
import { msaFuzzyMatch } from '../utils/msaMatch';

const ORANGE = '#E36F2C';
const EQUIP_TYPES = ['Utilization','Non-Consignment','Third-Party'];
const LABOR_TYPES = ['Standard','Fixed','Subcontractor'];
const PHASES = ['Pre-TA','Mechanical','Post-TA'];
// Questionnaire questions answered with a calendar picker (stored ISO
// YYYY-MM-DD): Q3 start date, Q4 finish date, Q83 vendor quote expiration.
// Registered SELECTIVELY, not for every date-sounding question: a picker can
// only hold one date, so a question whose honest answer is a list or a caveat
// must stay free text. Deliberate exception — Q162 "Roster Due Date" (MUSTER),
// where per-company answers like "GC by 8/1, subs by 8/8" are normal.
// Re-read requestSeed.js before adding a number here; catalog questions get
// renumbered during review, and pinning a stale one silently gives the picker
// to a different question.
const DATE_QUESTIONS = new Set([3, 4, 83]);
// Consumable line categories — identical list on the estimate (Estimating.jsx).
const CONS_CATEGORIES = ['Consumable','Service','Charge','Mobilization','Air Sale'];

const STATUS_COLOR = {
  Draft:      { bg:'#F3F4F6', fg:'#374151' },
  Submitted:  { bg:'#DBEAFE', fg:'#1E40AF' },
  Estimated:  { bg:'#D1FAE5', fg:'#065F46' },
  Declined:   { bg:'#FEE2E2', fg:'#991B1B' },
};
const StatusBadge = ({ status }) => {
  const c = STATUS_COLOR[status] || STATUS_COLOR.Draft;
  return (
    <span style={{ padding:'2px 10px', borderRadius:10, fontSize:11, fontWeight:700,
      background:c.bg, color:c.fg }}>
      {status}
    </span>
  );
};

const MSA_STATUS_COLOR = {
  Draft:     { bg:'#FEF3C7', fg:'#92400E' },
  Confirmed: { bg:'#DBEAFE', fg:'#1E40AF' },
  Verified:  { bg:'#D1FAE5', fg:'#065F46' },
};

export default function Requests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminLike(user);

  const titleRef = useRef(null);          // ✨ Suggest writes the composed title here
  const [catalog, setCatalog]           = useState({ scope_flags:[], questions:[] });
  const [customers, setCustomers]       = useState([]);
  const [projects, setProjects]         = useState([]);   // opportunity picker (role-scoped server-side)
  const [requests, setRequests]         = useState([]);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [listStatus, setListStatus]     = useState('');   // sidebar filter: '' = all
  const [listRep, setListRep]           = useState('');   // sidebar filter: salesperson name, '' = all
  const [files, setFiles]               = useState([]);
  const [fileDragOver, setFileDragOver] = useState(false);
  const [reps, setReps]                 = useState([]);   // reassign picker (admin/leadership/supervisor)
  const [history, setHistory]           = useState([]);
  const [msaSheet, setMsaSheet]         = useState(null);   // customer's current MSA rate sheet
  const [extracting, setExtracting]     = useState(false);
  const [msaConfigured, setMsaConfigured] = useState(true);
  const [current, setCurrent]           = useState(null);     // loaded request
  const [tab, setTab]                   = useState(isAdmin ? 'queue' : 'mine'); // mine | queue
  const [queueFilter, setQueueFilter]   = useState('Submitted');
  const [saving, setSaving]             = useState(false);
  const [savedAt, setSavedAt]           = useState(null);
  const [showRevisions, setShowRevisions] = useState(false);
  const [error, setError]               = useState('');
  const [expandedPhase, setExpandedPhase] = useState(null); // labor line id
  const [catalogQ, setCatalogQ]         = useState('');
  const [equipCatalog, setEquipCatalog] = useState([]);
  const [crafts, setCrafts]             = useState([]);   // active crafts → labor craft picker
  const [section, setSection]           = useState('questionnaire'); // questionnaire | labor | equipment | consumables
  const [fromEstimate, setFromEstimate] = useState(null); // {id, label} — set when arriving via View Request on the Estimating page

  // Draft answers (local — saved explicitly)
  const [draftAnswers, setDraftAnswers] = useState({}); // {question_n: {response, follow_up}}

  // ---- Boot ----------------------------------------------------------------
  useEffect(() => {
    api.getRequestCatalog().then(setCatalog).catch(() => {});
    api.lookupCrafts().then(setCrafts).catch(() => {});
    if (isAdmin || user.role === 'supervisor') api.listReps().then(setReps).catch(() => {});
    api.listCustomers({ status:'A', limit:5000 })
      .then(rows => setCustomers(rows))
      .catch(() => {});
    // Opportunity picker. The projects endpoint is already role-scoped, so a rep
    // only ever sees their own Project Tracker entries — plus UNASSIGNED ones
    // (include_unassigned), which is how an AM finds an opportunity a standalone
    // estimate created for them. Linking claims it into their tracker.
    api.listProjects({ include_unassigned: 'true' })
      .then(rows => setProjects(
        (rows || [])
          .filter(p => !['Lost','Closed','Complete'].includes(p.status))
          .sort((a, b) => String(a.site || '').localeCompare(String(b.site || '')))
      ))
      .catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    const params = {};
    if (tab === 'queue') params.status = queueFilter;
    if (showSuperseded) params.all_revisions = 'true';
    api.listRequests(params).then(setRequests).catch(e => setError(e.message));
  }, [tab, queueFilter, showSuperseded]);

  useEffect(() => { loadList(); }, [loadList]);

  // Deep link: /requests?open=<id> (e.g. View Request from the Estimating page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const open = Number(params.get('open'));
    if (open) loadRequest(open).catch(() => {});
    const fromEst = Number(params.get('from_est'));
    if (fromEst) setFromEstimate({ id: fromEst, label: params.get('from_est_label') || 'Estimate' });
    // ?from=estimating = a 👁 Review Request peek from the Waiting for Estimate queue
    else if (params.get('from') === 'estimating') setFromEstimate({ id: null, label: 'Estimating' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRequest = async (id) => {
    const r = await api.getRequest(id);
    setCurrent(r);
    // Populate draft answers from saved answers
    const map = {};
    for (const a of r.answers || []) {
      map[a.question_n] = { response: a.response || '', follow_up: a.follow_up || '' };
    }
    setDraftAnswers(map);
    setSection('questionnaire');
  };

  const loadEquipCatalog = useCallback(() => {
    api.listRequestEquipmentCatalog(catalogQ).then(setEquipCatalog).catch(() => {});
  }, [catalogQ]);
  useEffect(() => { loadEquipCatalog(); }, [loadEquipCatalog]);

  // Lazy-load files / audit history when their tab opens.
  useEffect(() => {
    if (!current) return;
    if (section === 'files')   api.listRequestFiles(current.id).then(setFiles).catch(() => setFiles([]));
    if (section === 'history') api.getRequestHistory(current.id).then(setHistory).catch(() => setHistory([]));
  }, [section, current?.id]);

  // MSA rate sheet for this request — chain-first resolution server-side
  // (this request's own extraction beats the customer-level fallback), loaded
  // whenever the request changes so the auto-populate pickers have it on hand.
  const loadMsaSheet = useCallback(async (requestId) => {
    if (!requestId) { setMsaSheet(null); return; }
    try { setMsaSheet(await api.getMsaSheetForRequest(requestId)); }
    catch { setMsaSheet(null); }
  }, []);
  useEffect(() => { loadMsaSheet(current?.id); }, [current?.id, current?.customer_id, loadMsaSheet]);
  useEffect(() => { api.msaStatus().then(s => setMsaConfigured(!!s.configured)).catch(() => {}); }, []);

  const extractMsa = () => {
    if (!current) return;
    setExtracting(true); setError('');
    api.extractMsaRates(current.id)
      .then(sheet => { setMsaSheet(sheet); setSection('msa'); })
      .catch(e => setError(e.message))
      .finally(() => setExtracting(false));
  };

  // MSA lines usable for auto-populate (AM has confirmed the mapping).
  const msaUsable = msaSheet && ['Confirmed','Verified'].includes(msaSheet.status);
  const msaLaborLines = msaUsable ? (msaSheet.lines || []).filter(l => l.kind === 'labor') : [];
  const msaEquipLines = msaUsable ? (msaSheet.lines || []).filter(l => l.kind === 'equipment') : [];

  const addLaborFromMsa = (line) => doSave(async () => {
    const r = await api.addRequestLabor(current.id, {
      craft: line.craft || line.msa_description,
      labor_type: 'Standard',
      qty_positions: 1,
      target_pay_st: line.pay_st != null ? Number(line.pay_st) : undefined,
      target_pay_ot: line.pay_ot != null ? Number(line.pay_ot) : undefined,
      target_bill_st: line.bill_st != null ? Number(line.bill_st) : undefined,
      target_bill_ot: line.bill_ot != null ? Number(line.bill_ot) : undefined,
      per_diem_eligible: line.per_diem != null,
      notes: `MSA rate (${line.msa_description})`,
    });
    setCurrent(r);
  });
  const addEquipFromMsa = (line) => doSave(async () => {
    const r = await api.addRequestEquipment(current.id, {
      description: line.catalog_description || line.msa_description,
      catalog_id: line.catalog_id || undefined,
      equip_type: 'Utilization', qty: 1, num_days: 1,
      target_day_rate: line.day_rate != null ? Number(line.day_rate) : undefined,
      msa_description: line.msa_description,
      notes: line.unit ? `MSA unit: ${line.unit}` : undefined,
    });
    setCurrent(r);
  });

  // ---- Helpers -------------------------------------------------------------
  const doSave = async (fn) => {
    setSaving(true); setError('');
    try { await fn(); setSavedAt(new Date()); }
    catch (e) {
      // The open request no longer exists server-side (deleted in another
      // tab or by leadership). Don't leave the AM editing a ghost — close
      // it, refresh the list, and say what happened.
      if (e.message === 'Not found' && current) {
        setCurrent(null);
        loadList();
        setError('This request no longer exists — it may have been deleted. Click + New Request to start a new one.');
      } else {
        setError(e.message);
      }
    }
    finally { setSaving(false); }
  };

  const reload = async (id) => {
    await loadRequest(id);
    loadList();
  };

  // ---- Create --------------------------------------------------------------
  const createRequest = async () => {
    doSave(async () => {
      // No seeded title — it used to stick as "New Request" forever (showed
      // even on Estimated rows). Created untitled; the AM names it (or it reads
      // "(untitled)" until they do).
      const r = await api.createRequest({});
      // Auto-fill Q5 (Project Salesperson) with the current user's name.
      // Still editable — just saves a step.
      await api.saveRequestAnswers(r.id, [
        { question_n: 5, section: 'PROJECT INFO', response: user.full_name, follow_up: '' },
      ]);
      await loadRequest(r.id);
      setTab('mine');
      loadList(); // setTab is a no-op when already on 'mine' — refresh explicitly
    });
  };

  // ---- Update header + scope flags -----------------------------------------
  const updateHeader = (body) => {
    if (!current) return;
    doSave(async () => {
      const r = await api.updateRequest(current.id, body);
      setCurrent(r);
      loadList();
    });
  };

  const toggleFlag = (flagKey) => {
    if (!current) return;
    const flags = Array.isArray(current.scope_flags) ? [...current.scope_flags] : [];
    const next = flags.includes(flagKey)
      ? flags.filter(f => f !== flagKey)
      : [...flags, flagKey];
    updateHeader({ scope_flags: next });
  };

  // ---- Visible questions ---------------------------------------------------
  // PROJECT INFO + SCOPE always show, plus QUOTING VERIFICATION and
  // Conclusion — those protect the estimate (stale vendor quotes and
  // undocumented assumptions) regardless of scope. Every other question
  // appears when ANY of its gate flags is selected: gate is a comma-separated
  // flag list (e.g. LABOR RULES applies to techs, safety pros, and rescue).
  const ALWAYS_ON_SECTIONS = ['PROJECT INFO', 'SCOPE', 'QUOTING VERIFICATION', 'Conclusion'];
  const activeFlags = current ? (Array.isArray(current.scope_flags) ? current.scope_flags : []) : [];
  // Editable draft with NO scope selected shows NO questions at all — not even
  // the always-on sections (2026-07-23, George: sales answered whatever was
  // visible and never picked a scope). Read-only views (submitted/superseded
  // legacy rows without flags) keep rendering their answered sections.
  const noScopeYet = !!current && current.status === 'Draft' && current.is_current !== false
    && activeFlags.length === 0;
  const visibleQs = noScopeYet ? [] : catalog.questions.filter(q =>
    q.gate
      ? q.gate.split(',').some(g => activeFlags.includes(g.trim()))
      : ALWAYS_ON_SECTIONS.includes(q.section)
  );
  // Sections render in question_n order, EXCEPT Conclusion, which is pinned
  // last. Sections added after Conclusion shipped (MUSTER, Q149-170) are
  // numbered past it because question_n is frozen once answers exist — so the
  // ordering fix lives here, not in the catalog. Pin any future closing
  // section the same way rather than renumbering the catalog.
  const CLOSING_SECTIONS = ['Conclusion'];
  const sectionNames = [...new Set(visibleQs.map(q => q.section))]
    .sort((a, b) => (CLOSING_SECTIONS.includes(a) ? 1 : 0) - (CLOSING_SECTIONS.includes(b) ? 1 : 0));

  // ---- Save answers --------------------------------------------------------
  const saveAnswers = () => {
    if (!current) return;
    doSave(async () => {
      const answers = visibleQs.map(q => ({
        question_n: q.question_n,
        section: q.section,
        response: draftAnswers[q.question_n]?.response || '',
        follow_up: draftAnswers[q.question_n]?.follow_up || '',
      }));
      await api.saveRequestAnswers(current.id, answers);
      await reload(current.id);
    });
  };

  // ---- Import answers from the old estimate workbook ------------------------
  // The legacy estimator's "Project Information" sheet holds the same
  // questions this questionnaire was seeded from; the server matches on
  // question text and fills answers. Dry-run first, confirm, then apply.
  const importAnswersFile = (file) => {
    if (!current || !file) return;
    doSave(async () => {
      const prev = await api.importRequestAnswers(current.id, file, { dry: true });
      const unmatchedNote = prev.unmatched.length
        ? `\n${prev.unmatched.length} answered workbook row(s) had no matching question and will be left out.` : '';
      // Occupied line tabs: offer to REPLACE them (updated workbook flow).
      const blockedTabs = ['labor', 'equipment', 'consumables'].filter(k => prev[k]?.blocked_existing);
      let replaceLines = false;
      if (blockedTabs.length) {
        replaceLines = confirm(
          `The ${blockedTabs.join(' + ')} tab(s) already have lines.\n\n` +
          `REPLACE them with this workbook's lines? (OK = wipe those tabs and re-import — use for an updated workbook. ` +
          `Cancel = keep the existing lines and import only the rest.)`
        );
      }
      // Dates + line-tab summary (labor comes from the Labor Estimate input
      // table with phases, equipment/consumables from the Quote sheet).
      const extras = [];
      if (prev.dates?.start && prev.dates?.end) extras.push(`project dates ${prev.dates.start} → ${prev.dates.end}`);
      for (const [label, o] of [['labor', prev.labor], ['equipment', prev.equipment], ['consumables', prev.consumables]]) {
        if (!o || !o.found) continue;
        const phases = label === 'labor' && o.with_phases ? ' with phases' : '';
        if (o.blocked_existing && !replaceLines) extras.push(`${label}: ${o.found} line(s) found but the tab already has lines — skipped`);
        else if (o.blocked_existing && replaceLines) extras.push(`${label}: ${o.found} line(s)${phases} — REPLACING the existing lines`);
        else extras.push(`${label}: ${o.found} line(s)${phases}`);
      }
      const extraNote = extras.length ? `\nAlso importing: ${extras.join('; ')}.` : '';
      const anyLines = ['labor', 'equipment', 'consumables'].some(k => prev[k]?.imported > 0) || (replaceLines && blockedTabs.length > 0);
      if (!prev.filled.length && !prev.skipped_existing.length && !anyLines) {
        alert(`Nothing to import from "${file.name}".${unmatchedNote}${extraNote}`);
        return;
      }
      if (!prev.filled.length && !anyLines) {
        if (!confirm(`All ${prev.skipped_existing.length} matched question(s) already have answers.\n\nOverwrite them with the workbook values?${unmatchedNote}`)) return;
        await api.importRequestAnswers(current.id, file, { overwrite: true });
        await reload(current.id);
        return;
      }
      const keptNote = prev.skipped_existing.length
        ? `\n${prev.skipped_existing.length} question(s) already answered will be kept.` : '';
      if (!confirm(`Import from "${file.name}" — ${prev.filled.length} answer(s).${extraNote}${keptNote}${unmatchedNote}\n\nUnsaved edits on this questionnaire will be reloaded from the server.`)) return;
      await api.importRequestAnswers(current.id, file, { replaceLines });
      if (prev.skipped_existing.length &&
          confirm(`Imported.\n\nAlso overwrite the ${prev.skipped_existing.length} already-answered question(s) with the workbook values?`)) {
        await api.importRequestAnswers(current.id, file, { overwrite: true });
      }
      await reload(current.id);
    });
  };

  // ---- Submit --------------------------------------------------------------
  const submitRequest = () => {
    if (!current) return;
    if (!confirm('Submit this request for review? You will not be able to edit it after submission.')) return;
    // Auto-save answers first, then submit
    doSave(async () => {
      const answers = visibleQs.map(q => ({
        question_n: q.question_n, section: q.section,
        response: draftAnswers[q.question_n]?.response || '',
        follow_up: draftAnswers[q.question_n]?.follow_up || '',
      }));
      await api.saveRequestAnswers(current.id, answers);
      await api.submitRequest(current.id);
      await reload(current.id);
    });
  };

  // ---- Create Estimate (leadership) ----------------------------------------
  const createEstimate = async (id) => {
    doSave(async () => {
      const r = await api.createEstimateFromRequest(id);
      // Only surprising when the button said "Create" — if the request already
      // showed an estimate the user asked to open it.
      if (r.already_existed && !current?.estimate_id) {
        alert(`An estimate already exists for this request (estimate #${r.estimate_id}). Opening it now.`);
      }
      navigate(`/estimating?open=${r.estimate_id}`);
      await reload(id);
    });
  };

  // ---- Revise (change request from the client) ------------------------------
  const reviseRequest = () => {
    if (!current) return;
    if (!confirm(`Create revision -${Number(current.revision || 0) + 1} of this request?\n\nThe current revision freezes as the record of what was submitted; the new one opens as an editable Draft for the client's change request.`)) return;
    doSave(async () => {
      const r = await api.reviseRequest(current.id);
      setCurrent(r);
      const map = {};
      for (const a of r.answers || []) map[a.question_n] = { response: a.response || '', follow_up: a.follow_up || '' };
      setDraftAnswers(map);
      loadList();
    });
  };

  // ---- CSVM vendor proposal --------------------------------------------------
  // Project Info + Scope + CSVM Proposal questionnaire sections as a PDF for
  // the CSVM vendor. Download, or email straight to the vendor (sender CC'd).
  const downloadCsvmProposal = () => {
    if (!current) return;
    doSave(() => api.exportCsvmProposal(current.id, reqNo(current)));
  };
  const emailCsvmProposal = () => {
    if (!current) return;
    const to = prompt("CSVM vendor email address (comma-separate multiple).\nYou will be CC'd on the send:");
    if (!to || !to.trim()) return;
    doSave(async () => {
      const r = await api.emailCsvmProposal(current.id, to.trim());
      alert(`CSVM proposal sent to ${r.to}${r.cc ? `\nCC: ${r.cc}` : ''}`);
    });
  };
  // Vendor's returned quote: upload as a normal chain-scoped request file,
  // then tag it as THE vendor quote on the chain.
  const uploadCsvmQuote = (file) => {
    if (!file || !current) return;
    doSave(async () => {
      const up = await api.uploadRequestFile(current.id, file);
      setCurrent(await api.updateRequestCsvmVendor(current.id, { quote_attachment_id: up.id }));
      if (section === 'files') setFiles(await api.listRequestFiles(current.id));
    });
  };

  // ---- Customer files --------------------------------------------------------
  const uploadFiles = async (fileList) => {
    const list = Array.from(fileList || []).filter(Boolean);
    if (!list.length || !current) return;
    doSave(async () => {
      for (const file of list) await api.uploadRequestFile(current.id, file);
      setFiles(await api.listRequestFiles(current.id));
    });
  };
  const deleteFile = (f) => {
    if (!confirm(`Delete "${f.original_name}"?`)) return;
    doSave(async () => {
      await api.deleteRequestFile(current.id, f.id);
      setFiles(await api.listRequestFiles(current.id));
    });
  };

  // ---- Delete request ------------------------------------------------------
  const deleteRequest = (id, label) => {
    if (!confirm(`Delete request "${label}"? This cannot be undone.`)) return;
    doSave(async () => {
      await api.deleteRequest(id);
      if (current?.id === id) setCurrent(null);
      loadList();
    });
  };

  // ---- Labor ---------------------------------------------------------------
  const addLabor = () => doSave(async () => { const r = await api.addRequestLabor(current.id, { craft:'Safety Tech', labor_type:'Standard', qty_positions:1 }); setCurrent(r); });
  const updateLabor = (lineId, body) => doSave(async () => { const r = await api.updateRequestLabor(lineId, body); setCurrent(r); });
  const deleteLabor = (lineId) => { if (!confirm('Delete?')) return; doSave(async () => { const r = await api.deleteRequestLabor(lineId); setCurrent(r); }); };
  const savePhases = (lineId, phases) => doSave(async () => { const r = await api.updateRequestLaborPhases(lineId, phases); setCurrent(r); setExpandedPhase(null); });

  // ---- Equipment -----------------------------------------------------------
  const addEquipFromCatalog = (item) => doSave(async () => {
    const r = await api.addRequestEquipment(current.id, {
      description: item.description, category: item.category, cat_class: item.cat_class,
      equip_type: item.default_type || 'Utilization', catalog_id: item.id, qty:1, num_days:1,
    });
    setCurrent(r); setCatalogQ('');
  });
  const addEquipManual = () => doSave(async () => {
    const r = await api.addRequestEquipment(current.id, {
      description: 'Equipment', equip_type: 'Utilization', qty:1, num_days:1,
    });
    setCurrent(r);
  });
  // Valero: MSA wording mandatory on labor + equipment (server/utils/msaWordingRule.js enforces on submit).
  const msaWordingRequired = /^\s*VALERO\b/i.test(current?.customer_name || current?.new_customer_name || '');
  const updateEquip = (lineId, body) => doSave(async () => { const r = await api.updateRequestEquipment(lineId, body); setCurrent(r); });
  const deleteEquip = (lineId) => { if (!confirm('Delete?')) return; doSave(async () => { const r = await api.deleteRequestEquipment(lineId); setCurrent(r); }); };
  // Clone a line keeping description / CAT-CLASS / rate so a qty ramp up/down is
  // modeled as separate dated lines (mirrors the estimate Dup).
  const dupEquip = (line) => doSave(async () => {
    const r = await api.addRequestEquipment(current.id, {
      description: line.description, category: line.category, cat_class: line.cat_class,
      equip_type: line.equip_type, catalog_id: line.catalog_id || undefined,
      qty: line.qty, num_days: line.num_days,
      start_date: line.start_date ? String(line.start_date).slice(0,10) : null,
      end_date: line.end_date ? String(line.end_date).slice(0,10) : null,
      target_day_rate: line.target_day_rate, msa_description: line.msa_description || null, notes: line.notes || null,
    });
    setCurrent(r);
  });
  // Carry one line's Start/End (and derived Days) to every equipment line.
  const applyEquipDatesToAll = (srcLine) => {
    const start = srcLine.start_date ? String(srcLine.start_date).slice(0,10) : '';
    const end = srcLine.end_date ? String(srcLine.end_date).slice(0,10) : '';
    if (!start || !end) { alert("Set this line's Start and End dates first."); return; }
    const ids = (current.equipment_lines || []).map(l => l.id);
    if (!ids.length) return;
    if (!confirm(`Apply ${start} → ${end} to all ${ids.length} equipment line(s)?`)) return;
    doSave(async () => {
      const r = await api.applyRequestEquipmentDates(current.id, { start_date: start, end_date: end, line_ids: ids });
      setCurrent(r);
    });
  };

  // ---- Consumables ---------------------------------------------------------
  const addCons = () => doSave(async () => { const r = await api.addRequestConsumable(current.id, { description:'Item', qty:1 }); setCurrent(r); });
  const updateCons = (lineId, body) => doSave(async () => { const r = await api.updateRequestConsumable(lineId, body); setCurrent(r); });
  const deleteCons = (lineId) => { if (!confirm('Delete?')) return; doSave(async () => { const r = await api.deleteRequestConsumable(lineId); setCurrent(r); }); };
  // Set Target Bill Rate = Cost × (1 + default markup) on every line with a cost.
  const applyConsMarkup = () => {
    const pctLabel = (consMarkup * 100).toFixed(1).replace(/\.0$/, '');
    if (!confirm(`Set Target Bill Rate = Cost × (1 + ${pctLabel}%) on every line with a cost? This overwrites their current bill rates.`)) return;
    doSave(async () => { const r = await api.applyRequestConsumableMarkup(current.id, consMarkup); setCurrent(r); });
  };

  // ---- Derived -------------------------------------------------------------
  const consMarkup = Number(current?.consumable_markup_pct) || 0;  // markup-over-cost default for the Consumables tab
  const isSuperseded = current && current.is_current === false;
  const isReadOnly = current && (current.status !== 'Draft' || isSuperseded);
  const isLeadership = isAdmin;
  const customerLabel = current
    ? (current.customer_name || current.new_customer_name || '(no customer)')
    : '';
  const reqNo = (r) => `REQ-${r.root_id || r.id}-${r.revision || 0}`;
  // Amber attention style for the two header fields the AM must fill first
  const needsAttention = { border:'2px solid #F59E0B', background:'#FFFBEB' };
  const customerMissing = current && !current.customer_id && !current.new_customer_name;

  // ---- Title: mandatory, and it should say WHERE and WHAT --------------------
  // George 2026-08-17: "A title should be mandatory, should have Location/Scope."
  // The server blocks a blank submit; this composes a conforming one so nobody
  // has to invent the convention. Whether free text truly names a location is
  // not checkable, so there is no fake validation here — just a good default.
  const titleMissing = current && !reqDisplayTitle(current.title);

  // Q2 is "Customer / Location Address" — usually a full postal address, which
  // is too long for a title. Prefer the trailing "City, ST" when the answer
  // looks like an address; otherwise use it as typed.
  const shortLocation = () => {
    const raw = String(draftAnswers[2]?.response || '').split('\n')[0].trim();
    if (!raw) return '';
    if (raw.length <= 28) return raw;
    const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const tail = parts.slice(-2).join(', ');
      // Drop a trailing ZIP so "Sabine Pass, TX 77655" reads "Sabine Pass, TX".
      return tail.replace(/\s+\d{5}(-\d{4})?$/, '');
    }
    return raw.slice(0, 28).trim();
  };

  // Scope labels are long ("Safety Technicians / Hole Watch / Fire Watch") —
  // the first segment is the recognisable part. Two is enough to identify a job.
  const shortScope = () => {
    const labels = (current?.scope_flags || [])
      .map(k => (catalog.scope_flags.find(f => f.flag_key === k || f.key === k) || {}))
      .map(f => String(f.label || '').split('/')[0].trim())
      .filter(Boolean);
    if (!labels.length) return '';
    return labels.slice(0, 2).join(' + ') + (labels.length > 2 ? ` +${labels.length - 2}` : '');
  };

  const suggestTitle = () => {
    const parts = [
      current?.customer_name || current?.new_customer_name || '',
      shortLocation(),
      shortScope(),
    ].map(s => String(s || '').trim()).filter(Boolean);
    return parts.join(' — ');
  };
  const canRevise = current && !isSuperseded
    && ['Submitted','Estimated'].includes(current.status)
    && (isAdmin || current.created_by === user.id);

  // Sidebar filters run client-side over the loaded list (no pagination, so
  // nothing is hidden server-side). Salesperson = the name shown on the card:
  // the owning AM, or the FS PM for change orders.
  const repNameOf = (r) => r.created_by_name || (r.external_submitter_name ? `${r.external_submitter_name} (FS PM)` : '');
  const repOptions = Array.from(new Set(requests.map(repNameOf).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const visibleRequests = requests.filter(r =>
    (!listStatus || r.status === listStatus) && (!listRep || repNameOf(r) === listRep)
  );
  const listFiltered = !!(listStatus || listRep);

  // ---- Render --------------------------------------------------------------
  return (
    <>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:12 }}>
        <h1 style={{ ...styles.h1, margin:0 }}>Requests</h1>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {isAdmin && (
            <div style={{ display:'flex', gap:4, alignItems:'center' }}>
              <button style={tab==='mine' ? styles.tabBtnActive : styles.tabBtn} onClick={() => setTab('mine')}>My Requests</button>
              <button style={tab==='queue' ? styles.tabBtnActive : styles.tabBtn} onClick={() => setTab('queue')}>Queue</button>
              <span
                title={"My Requests: drafts and requests you've created.\nQueue: requests submitted by reps waiting for you to review and create an estimate.\n\nFilter:\n  Submitted = needs pricing (your action queue)\n  Estimated = already handled (for reference)\n  All = everything"}
                style={{ cursor:'help', fontSize:14, color:'#9CA3AF', marginLeft:4 }}
              >(?)</span>
            </div>
          )}
          {tab === 'queue' && isAdmin && (
            <select style={{ ...styles.input, width:160 }} value={queueFilter} onChange={e => setQueueFilter(e.target.value)}>
              <option value="Submitted">Submitted</option>
              <option value="Estimated">Estimated</option>
              <option value="">All</option>
            </select>
          )}
          <RequestGuide />
          <button style={styles.btn} disabled={saving} onClick={createRequest}>+ New Request</button>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={{ display:'grid', gridTemplateColumns: current ? '300px 1fr' : '1fr', gap:16 }}>
        {/* Sidebar list */}
        <div style={styles.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <h2 style={{ ...styles.h2, margin:0 }}>
              {tab === 'queue' ? 'Queue' : 'My Requests'}
            </h2>
            <label style={{ fontSize:10, color:'#6B7280', display:'flex', alignItems:'center', gap:3, cursor:'pointer' }}>
              <input type="checkbox" checked={showSuperseded} onChange={e => setShowSuperseded(e.target.checked)} />
              All revisions
            </label>
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
            {tab !== 'queue' && (
              <select style={{ ...styles.input, flex:1, minWidth:110, fontSize:12, padding:'4px 6px' }}
                value={listStatus} onChange={e => setListStatus(e.target.value)} title="Filter by status">
                <option value="">All statuses</option>
                {Object.keys(STATUS_COLOR).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <select style={{ ...styles.input, flex:1, minWidth:110, fontSize:12, padding:'4px 6px' }}
              value={listRep} onChange={e => setListRep(e.target.value)} title="Filter by salesperson">
              <option value="">All salespeople</option>
              {repOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            {listFiltered && (
              <button type="button" title="Clear filters"
                onClick={() => { setListStatus(''); setListRep(''); }}
                style={{ background:'transparent', border:'none', color:'#6B7280', cursor:'pointer', fontSize:12, padding:'2px 4px' }}>
                ✕
              </button>
            )}
          </div>
          {visibleRequests.length === 0 && (
            <div style={{ color:'#9CA3AF', fontSize:12 }}>
              {listFiltered ? 'No requests match these filters.'
                : tab==='queue' ? 'No requests in this status.' : 'No requests yet. Click + New Request.'}
            </div>
          )}
          {visibleRequests.map(r => (
            <div key={r.id}
              style={{
                padding:'8px 10px', borderRadius:6, marginBottom:4,
                background: current?.id === r.id ? '#FFF7ED' : '#F9FAFB',
                border:`1px solid ${current?.id === r.id ? ORANGE : '#E5E7EB'}`,
                fontSize:13,
              }}
            >
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:4 }}>
                <div style={{ cursor:'pointer', flex:1, opacity: r.is_current === false ? 0.6 : 1 }} onClick={() => loadRequest(r.id)}>
                  <div style={{ fontWeight:600, color: ORANGE }}>
                    {reqDisplayTitle(r.title) || '(untitled)'}
                    <span style={{ color:'#9CA3AF', fontWeight:600, fontSize:10, marginLeft:6 }}>
                      REQ-{r.root_id || r.id}-{r.revision || 0}
                    </span>
                  </div>
                  <div style={{ color:'#6B7280', fontSize:11 }}>
                    {r.customer_name || r.new_customer_name || 'No customer'} · {r.created_by_name || (r.external_submitter_name ? `${r.external_submitter_name} (FS PM)` : '')}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:3, flexWrap:'wrap' }}>
                    <StatusBadge status={r.status} />
                    {r.kind === 'change_order' && (
                      <span title="Change order submitted by the onsite PM from Field Services"
                        style={{ fontSize:10, fontWeight:600, color:'#6B21A8', background:'#F3E8FF', padding:'1px 6px', borderRadius:8 }}>
                        🔁 CO
                      </span>
                    )}
                    {r.is_current === false && (
                      <span style={{ fontSize:10, fontWeight:600, color:'#6B7280', background:'#F3F4F6', padding:'1px 6px', borderRadius:8 }}>
                        Superseded
                      </span>
                    )}
                    {!r.project_id && (
                      <span title="Link a Project Tracker opportunity — required before submit"
                        style={{ fontSize:10, fontWeight:600, color:'#92400E', background:'#FEF3C7', padding:'1px 6px', borderRadius:8 }}>
                        ⚠ No opportunity
                      </span>
                    )}
                    {r.quote_due_by && (
                      <span style={{ fontSize:10, fontWeight:600, color:'#92400E', background:'#FEF3C7', padding:'1px 6px', borderRadius:8 }}>
                        Due {new Date(r.quote_due_by).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                      </span>
                    )}
                  </div>
                </div>
                {r.status === 'Draft' && (
                  <button
                    title="Delete this request"
                    onClick={ev => { ev.stopPropagation(); deleteRequest(r.id, reqDisplayTitle(r.title) || '(untitled)'); }}
                    style={{ background:'transparent', border:'none', color:'#EF4444', cursor:'pointer', fontSize:15, lineHeight:1, padding:'2px 4px', flexShrink:0 }}
                  >🗑</button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Request detail */}
        {current && (
          <div>
            {/* Superseded banner */}
            {isSuperseded && (
              <div style={{ background:'#FEF3C7', border:'1px solid #FBBF24', borderRadius:6,
                padding:'8px 14px', marginBottom:12, fontSize:13, fontWeight:600, color:'#92400E' }}>
                ⚠ This is revision -{current.revision || 0}, superseded by a newer revision. Read-only.
              </div>
            )}

            {/* Header card */}
            <div style={{ ...styles.card, marginBottom:12 }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:16, alignItems:'flex-end', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontSize:11, color:'#6B7280', marginBottom:3 }}>
                    REQUEST <span style={{ fontWeight:700, color:'#374151' }}>{reqNo(current)}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    {isReadOnly
                      ? <div style={{ fontWeight:700, fontSize:16 }}>{reqDisplayTitle(current.title) || '(untitled)'}</div>
                      : <>
                          <input style={{ ...styles.input, fontSize:15, fontWeight:700, width:360,
                            ...(titleMissing ? needsAttention : {}) }}
                            key={current.id}
                            defaultValue={reqDisplayTitle(current.title)}
                            ref={titleRef}
                            placeholder="Customer — Location — Scope  (required)"
                            title="Required. Name it so leadership can pick it out of a queue: customer, where the work is, and what the scope is."
                            onBlur={e => updateHeader({ title: e.target.value })} />
                          <button type="button" style={{ ...styles.btnSecondary, fontSize:11, padding:'5px 9px' }}
                            disabled={saving}
                            title="Build a title from the customer, the location in Q2, and the scope you picked"
                            onClick={() => {
                              const s = suggestTitle();
                              if (!s) { alert('Pick the customer, answer Q2 (location), and select at least one scope option — then Suggest can build the title.'); return; }
                              if (titleRef.current) titleRef.current.value = s;
                              updateHeader({ title: s });
                            }}>
                            ✨ Suggest
                          </button>
                        </>
                    }
                    <StatusBadge status={current.status} />
                  </div>
                  {!isReadOnly && titleMissing && (
                    <div style={{ fontSize:11, color:'#B45309', fontWeight:600, marginTop:3 }}>
                      ⚠ A title is required to submit — include the location and the scope
                    </div>
                  )}
                  <div style={{ fontSize:12, color:'#9CA3AF', marginTop:4, display:'flex', alignItems:'center', gap:8 }}>
                    {customerLabel}
                    {saving && <span style={{ color:'#9CA3AF' }}>Saving…</span>}
                    {!saving && savedAt && (
                      <span style={{ color:'#10B981', fontWeight:600 }}>
                        ✓ Saved {savedAt.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}
                      </span>
                    )}
                    <button
                      style={{ ...styles.btnSecondary, fontSize:11, padding:'2px 8px' }}
                      onClick={() => setShowRevisions(true)}
                    >View History</button>
                    {(isAdmin || user.role === 'supervisor') && reps.length > 0 && (
                      <label style={{ display:'flex', alignItems:'center', gap:4 }}
                        title="Account Manager who owns this request — it appears under their My Requests. Reassigning moves the whole revision chain.">
                        👤
                        <select style={{ ...styles.input, fontSize:11, padding:'2px 6px', width:150 }}
                          value={current.created_by || ''}
                          onChange={e => {
                            const uid = Number(e.target.value);
                            if (!uid || uid === current.created_by) return;
                            const name = reps.find(x => x.id === uid)?.full_name || 'this user';
                            if (!confirm(`Reassign this request (all revisions) to ${name}?`)) { e.target.value = current.created_by; return; }
                            doSave(async () => { setCurrent(await api.reassignRequest(current.id, uid)); loadList(); });
                          }}>
                          {!reps.some(x => x.id === current.created_by) && (
                            <option value={current.created_by || ''}>{current.created_by_name || '—'}</option>
                          )}
                          {reps.map(x => <option key={x.id} value={x.id}>{x.full_name}</option>)}
                        </select>
                      </label>
                    )}
                  </div>
                  {/* Who the customer calls. The quote's Contact block is built
                      from this person's user row, so the gap is fixable right
                      here instead of through an admin. */}
                  <AmContactLine
                    request={current}
                    onPhoneSaved={phone => setCurrent(c => (c ? { ...c, created_by_phone: phone } : c))}
                  />
                </div>

                {/* Customer picker */}
                {!isReadOnly && (
                  <div>
                    <div style={{ fontSize:11, color: customerMissing ? '#B45309' : '#6B7280', fontWeight: customerMissing ? 700 : 400, marginBottom:3 }}>
                      CUSTOMER
                      <span style={{ color:'#EF4444', marginLeft:4 }}>*</span>
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <select style={{ ...styles.input, width:240, fontSize:13, ...(customerMissing ? needsAttention : {}) }}
                        value={current.customer_id || ''}
                        onChange={e => updateHeader({ customer_id: e.target.value || null, new_customer_name: null })}>
                        <option value="">— Select customer —</option>
                        <option value="">Customer not listed (enter below)</option>
                        {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      {!current.customer_id && (
                        <input style={{ ...styles.input, width:200, fontSize:13 }}
                          placeholder="Customer name (not in CRM)"
                          defaultValue={current.new_customer_name || ''}
                          onBlur={e => updateHeader({ new_customer_name: e.target.value })} />
                      )}
                    </div>
                    {customerMissing && (
                      <div style={{ fontSize:10, color:'#B45309', marginTop:2 }}>
                        Start here — pick the customer (or type the name if not in CRM).
                      </div>
                    )}
                  </div>
                )}

                {/* Opportunity (Project Tracker) link — required before submit */}
                <div>
                  <div style={{ fontSize:11, color: !isReadOnly && !current.project_id ? '#B45309' : '#6B7280', fontWeight: !isReadOnly && !current.project_id ? 700 : 400, marginBottom:3 }}>
                    OPPORTUNITY
                    {!isReadOnly && <span style={{ color:'#EF4444', marginLeft:4 }}>*</span>}
                  </div>
                  {isReadOnly ? (
                    <div style={{ fontSize:13, fontWeight:600, color: current.project_id ? '#1F2937' : '#EF4444' }}>
                      {current.project_site || (current.project_id ? `Project #${current.project_id}` : 'Not linked')}
                    </div>
                  ) : (
                    <select style={{ ...styles.input, width:240, fontSize:13, ...(!current.project_id ? needsAttention : {}) }}
                      value={current.project_id || ''}
                      onChange={e => updateHeader({ project_id: e.target.value ? Number(e.target.value) : null })}>
                      <option value="">— Link your Project Tracker entry —</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.site} ({p.status}){!p.account_owner_id ? ' · unassigned' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {!current.project_id && (
                    <div style={{ fontSize:10, color:'#B45309', marginTop:2 }}>
                      Required — the estimate, quote, and customer files hang off the opportunity.
                    </div>
                  )}
                </div>

                {/* Quote due by */}
                <div>
                  <div style={{ fontSize:11, color:'#6B7280', marginBottom:3 }}>
                    QUOTE NEEDED BY
                    {!isReadOnly && <span style={{ color:'#EF4444', marginLeft:4 }}>*</span>}
                  </div>
                  {isReadOnly ? (
                    <div style={{ fontSize:13, fontWeight:600, color: current.quote_due_by ? '#1F2937' : '#EF4444' }}>
                      {current.quote_due_by
                        ? new Date(current.quote_due_by).toLocaleString('en-US', { dateStyle:'medium', timeStyle:'short' })
                        : 'Not set'}
                    </div>
                  ) : (
                    <input
                      type="datetime-local"
                      style={{ ...styles.input, fontSize:13, width:200 }}
                      defaultValue={current.quote_due_by
                        ? new Date(new Date(current.quote_due_by).getTime()
                            - new Date(current.quote_due_by).getTimezoneOffset() * 60000)
                            .toISOString().slice(0,16)
                        : ''}
                      onBlur={e => updateHeader({ quote_due_by: e.target.value || null })}
                    />
                  )}
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                  {fromEstimate && (
                    <button style={styles.btnSecondary}
                      title={fromEstimate.id ? 'Return to the estimate you came from' : 'Return to the Estimating page'}
                      onClick={() => navigate(fromEstimate.id ? `/estimating?open=${fromEstimate.id}` : '/estimating')}>
                      ← Back to {fromEstimate.label}
                    </button>
                  )}
                  {!isReadOnly && (
                    <>
                      <button style={styles.btnSecondary} disabled={saving} onClick={saveAnswers}>
                        💾 Save Draft
                      </button>
                      <button
                        style={{ ...styles.btn, opacity: current.project_id && activeFlags.length && !titleMissing ? 1 : 0.5 }}
                        disabled={saving || !current.project_id || !activeFlags.length || titleMissing}
                        title={!current.project_id ? 'Link a Project Tracker opportunity first'
                          : !activeFlags.length ? 'Select at least one scope option first'
                          : titleMissing ? 'Name this request first — include the location and the scope (✨ Suggest builds one)' : ''}
                        onClick={submitRequest}>
                        Submit
                      </button>
                    </>
                  )}
                  {canRevise && (
                    <button style={{ ...styles.btnSecondary, fontWeight:700 }} disabled={saving}
                      title="Client sent a change request — clone this into an editable new revision"
                      onClick={reviseRequest}>
                      📝 New Revision (-{Number(current.revision || 0) + 1})
                    </button>
                  )}
                  {activeFlags.includes('csvm') && (
                    <>
                      <button style={styles.btnSecondary} disabled={saving}
                        title="Download the Project Info + Scope + CSVM Proposal sections as a PDF for the CSVM vendor"
                        onClick={downloadCsvmProposal}>
                        ⬇ CSVM Proposal
                      </button>
                      <button style={styles.btnSecondary} disabled={saving}
                        title="Email the CSVM proposal PDF to the vendor — you are CC'd"
                        onClick={emailCsvmProposal}>
                        📧 Email CSVM Vendor
                      </button>
                    </>
                  )}
                  {isLeadership && current.status === 'Submitted' && !isSuperseded && (
                    <>
                      <button style={{ ...styles.btnSecondary, border:'1px solid #F59E0B', color:'#B45309' }} disabled={saving}
                        title="Send this back to the AM as a Draft with a note explaining what's missing — they get an email"
                        onClick={() => {
                          const note = prompt("What's missing or needs more information? (sent to the AM)");
                          if (!note || !note.trim()) return;
                          doSave(async () => { setCurrent(await api.returnRequest(current.id, note.trim())); loadList(); });
                        }}>
                        ↩ Return to AM
                      </button>
                      {/* An estimate already started from a still-Submitted
                          request means it was opened but never worked — the
                          request stays in leadership's queue until it is priced. */}
                      <button style={{ ...styles.btn, background: current.estimate_id ? '#1F2937' : '#065F46' }} disabled={saving}
                        title={current.estimate_id
                          ? 'Opens the estimate already started from this request — it is still waiting to be priced'
                          : 'Creates the estimate from this request and opens it. Viewing it does not take the request out of the estimating queue.'}
                        onClick={() => createEstimate(current.id)}>
                        {current.estimate_id ? '📊 Open Estimate →' : '📊 Create Estimate'}
                      </button>
                    </>
                  )}
                  {isLeadership && current.status !== 'Submitted' && current.estimate_id && (
                    <button style={styles.btnSecondary} onClick={() => navigate(`/estimating?open=${current.estimate_id}`)}>
                      📊 View Estimate →
                    </button>
                  )}
                </div>
              </div>

              {/* Scope flags */}
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>
                  Scope — select all that apply
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {catalog.scope_flags.map(f => {
                    const on = activeFlags.includes(f.flag_key);
                    return (
                      <button key={f.flag_key} type="button"
                        disabled={isReadOnly}
                        onClick={() => toggleFlag(f.flag_key)}
                        title={f.covers || ''}
                        style={{
                          padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                          cursor: isReadOnly ? 'default' : 'pointer', border:'1px solid',
                          background: on ? '#1F2937' : '#F9FAFB',
                          color: on ? '#fff' : '#374151',
                          borderColor: on ? '#1F2937' : '#D1D5DB',
                        }}>
                        {on ? '✓ ' : ''}{f.label}
                      </button>
                    );
                  })}
                </div>
                {activeFlags.length > 0 && (
                  <div style={{ fontSize:11, color:'#6B7280', marginTop:6 }}>
                    {visibleQs.length} questions active ({visibleQs.filter(q=>q.gate).length} from scope flags)
                  </div>
                )}
              </div>
            </div>

            {/* CSVM vendor response — the vendor's quote + the SSS PO# issued
                to them. Editable at ANY status (the quote lands after submit);
                shared with the estimate page and the Operations Job Hub. */}
            {activeFlags.includes('csvm') && (
              <div style={{ ...styles.card, marginBottom:8, display:'flex', gap:16, alignItems:'center', flexWrap:'wrap' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  CSVM Vendor
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:11, color:'#6B7280' }}>SSS PO #</span>
                  <input style={{ ...styles.input, width:150, fontSize:13 }}
                    key={`csvmpo-${current.id}-${current.csvm_po_number || ''}`}
                    defaultValue={current.csvm_po_number || ''}
                    disabled={isSuperseded || saving}
                    placeholder="PO number"
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v === (current.csvm_po_number || '')) return;
                      doSave(async () => { setCurrent(await api.updateRequestCsvmVendor(current.id, { po_number: v || null })); });
                    }} />
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:11, color:'#6B7280' }}>Vendor Quote #</span>
                  <input style={{ ...styles.input, width:150, fontSize:13 }}
                    key={`csvmqn-${current.id}-${current.csvm_vendor_quote_number || ''}`}
                    defaultValue={current.csvm_vendor_quote_number || ''}
                    disabled={isSuperseded || saving}
                    placeholder="Vendor's quote number"
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v === (current.csvm_vendor_quote_number || '')) return;
                      doSave(async () => { setCurrent(await api.updateRequestCsvmVendor(current.id, { vendor_quote_number: v || null })); });
                    }} />
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:11, color:'#6B7280' }}>Vendor quote</span>
                  {current.csvm_vendor_quote_attachment_id ? (
                    <>
                      <button style={{ ...styles.btnSecondary, fontSize:12, padding:'3px 10px' }} disabled={saving}
                        title="Download the vendor's quote"
                        onClick={() => doSave(() => api.downloadRequestFile(current.id, current.csvm_vendor_quote_attachment_id, current.csvm_quote_name))}>
                        📄 {current.csvm_quote_name || 'quote file'}
                      </button>
                      {!isSuperseded && (
                        <button style={{ ...styles.btnDanger, fontSize:11, padding:'3px 8px' }} disabled={saving}
                          title="Untag this file as the vendor quote (the file itself stays on the Files tab)"
                          onClick={() => doSave(async () => { setCurrent(await api.updateRequestCsvmVendor(current.id, { quote_attachment_id: null })); })}>
                          ✕
                        </button>
                      )}
                    </>
                  ) : !isSuperseded ? (
                    <DropZone onFiles={files => uploadCsvmQuote(files[0])} disabled={saving} title="Drop the vendor quote here">
                      <label style={{ ...styles.btnSecondary, fontSize:12, padding:'3px 10px', cursor: saving ? 'wait' : 'pointer', display:'inline-block' }}>
                        {saving ? '…' : '⬆ Attach vendor quote'}
                        <input type="file" style={{ display:'none' }} disabled={saving}
                          onChange={e => { uploadCsvmQuote(e.target.files?.[0]); e.target.value=''; }} />
                      </label>
                    </DropZone>
                  ) : (
                    <span style={{ fontSize:12, color:'#9CA3AF' }}>none</span>
                  )}
                </div>
                <div style={{ fontSize:10, color:'#9CA3AF' }}>
                  Visible on the estimate and to Operations. The quote also lives on the Files tab.
                </div>
              </div>
            )}

            {/* FS change-order banner (2026-08-14): this request was submitted
                by the onsite PM from Field Services. No questionnaire — the
                reason + lines are the request; the estimate auto-links as a
                change order of the job's base estimate on handoff. */}
            {current.kind === 'change_order' && (
              <div style={{ background:'#F3E8FF', border:'1px solid #A78BFA', borderRadius:6,
                padding:'10px 14px', marginBottom:8, fontSize:13, color:'#5B21B6' }}>
                <strong>🔁 Change Order request from Field Services</strong>
                {current.external_submitter_name ? ` — submitted by ${current.external_submitter_name}` : ''}
                {current.external_submitter_email ? ` (${current.external_submitter_email})` : ''}
                {current.co_reason && (
                  <div style={{ marginTop:4, whiteSpace:'pre-wrap', color:'#1F2937' }}>
                    <strong>Reason:</strong> {current.co_reason}
                  </div>
                )}
                <div style={{ marginTop:4, fontSize:11, color:'#7C3AED' }}>
                  No questionnaire on a change order — the lines carry the PM's target rates.
                  Building the estimate auto-links it as a CO of the job's base estimate, and the
                  Approve/Reject decision emails the PM directly.
                </div>
              </div>
            )}

            {/* Returned-for-revision banner — what leadership needs before resubmit */}
            {current.return_note && current.status === 'Draft' && (
              <div style={{ background:'#FFFBEB', border:'1px solid #F59E0B', borderRadius:6,
                padding:'10px 14px', marginBottom:8, fontSize:13, color:'#92400E' }}>
                <strong>↩ Returned for revision</strong>
                {current.returned_by_name ? ` by ${current.returned_by_name}` : ''}
                {current.returned_at ? ` on ${fmtDate(current.returned_at)}` : ''}:
                <div style={{ marginTop:4, whiteSpace:'pre-wrap' }}>{current.return_note}</div>
                <div style={{ marginTop:4, fontSize:11, color:'#B45309' }}>Address the items above, then Submit again — this banner clears on resubmit.</div>
              </div>
            )}

            {/* Section tabs */}
            <div style={{ display:'flex', gap:4, marginBottom:8 }}>
              {['questionnaire','labor','equipment','consumables','msa','files','history'].map(s => (
                <button key={s} type="button" onClick={() => setSection(s)} style={{
                  padding:'6px 16px', borderRadius:4, fontSize:12, fontWeight:600,
                  cursor:'pointer', border:'none',
                  background: section===s ? '#1F2937' : '#E5E7EB',
                  color: section===s ? '#fff' : '#374151',
                }}>
                  {s === 'msa' ? 'MSA Rates' : s === 'consumables' ? 'Consumables, Services and Charges' : s.charAt(0).toUpperCase()+s.slice(1)}
                  {s === 'msa' && msaSheet && (
                    <span style={{ marginLeft:5, fontSize:9, padding:'1px 5px', borderRadius:6,
                      background: (MSA_STATUS_COLOR[msaSheet.status]||{}).bg, color: (MSA_STATUS_COLOR[msaSheet.status]||{}).fg }}>
                      {msaSheet.status}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Questionnaire */}
            {section === 'questionnaire' && (
              <div style={styles.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <h2 style={{ ...styles.h2, margin:0 }}>Questionnaire</h2>
                  {!isReadOnly && (
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <DropZone onFiles={files => importAnswersFile(files[0])} disabled={saving}
                        title="Drop the old estimate workbook (.xlsm) here — answers on its Project Information sheet fill the matching questions">
                        <label
                          style={{ ...styles.btnSecondary, cursor:'pointer', display:'inline-block', opacity: saving ? 0.6 : 1, border:'1px dashed #E36F2C' }}
                          title="Click to browse or drag the old estimate workbook (.xlsm) here"
                        >
                          ⬆ Import from old estimate — click or drop .xlsm
                          <input
                            type="file"
                            accept=".xlsm,.xlsx"
                            style={{ display:'none' }}
                            disabled={saving}
                            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importAnswersFile(f); }}
                          />
                        </label>
                      </DropZone>
                      <button style={styles.btn} onClick={saveAnswers} disabled={saving}>
                        💾 Save Answers
                      </button>
                    </div>
                  )}
                </div>
                {noScopeYet && current.kind === 'change_order' && (
                  <div style={{ color:'#6B7280', fontSize:13 }}>
                    Change orders carry no questionnaire — the PM's reason (banner above) and the
                    Labor / Equipment tabs are the request. Submitting needs no scope selection.
                  </div>
                )}
                {noScopeYet && current.kind !== 'change_order' && (
                  <div style={{ background:'#FEF3C7', border:'1px solid #FBBF24', borderRadius:6,
                    padding:'14px 16px', fontSize:13, fontWeight:600, color:'#92400E' }}>
                    ⚠ Select at least one scope option above — the questionnaire builds from what you pick.
                    Labor-only job? Pick <b>Labor</b>.
                  </div>
                )}
                {!noScopeYet && visibleQs.length === 0 && (
                  <div style={{ color:'#9CA3AF', fontSize:13 }}>
                    Select scope flags above to activate questions.
                  </div>
                )}
                {sectionNames.map(sectionName => {
                  const qs = visibleQs.filter(q => q.section === sectionName);
                  if (!qs.length) return null;
                  return (
                    <div key={sectionName} style={{ marginBottom:20 }}>
                      <div style={{
                        fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em',
                        color:'#fff', background: ORANGE, padding:'4px 12px', borderRadius:4,
                        marginBottom:10,
                      }}>
                        {sectionName}
                      </div>
                      {qs.map(q => (
                        <div key={q.question_n} style={{ marginBottom:14 }}>
                          <div style={{ fontSize:13, fontWeight:600, color:'#1F2937', marginBottom:4 }}>
                            <span style={{ color:'#9CA3AF', fontSize:11, marginRight:4 }}>Q{q.question_n}.</span>
                            {q.question}
                          </div>
                          {isReadOnly ? (
                            <div>
                              <div style={{ fontSize:13, background:'#F9FAFB', padding:'8px 10px', borderRadius:6, color:'#374151', minHeight:32 }}>
                                {(() => {
                                  const resp = current.answers?.find(a=>a.question_n===q.question_n)?.response;
                                  if (!resp) return <span style={{color:'#9CA3AF'}}>—</span>;
                                  // Date answers store ISO YYYY-MM-DD — show them human-readable.
                                  return DATE_QUESTIONS.has(q.question_n) && /^\d{4}-\d{2}-\d{2}$/.test(resp) ? fmtDate(resp) : resp;
                                })()}
                              </div>
                              {q.question_n === 7 && (
                                <MsaAttachment requestId={current.id} msa_filename={current.msa_filename}
                                  readOnly={true} onUploaded={() => {}} />
                              )}
                            </div>
                          ) : DATE_QUESTIONS.has(q.question_n) ? (
                            /* Date questions — calendar picker. A legacy
                               free-text answer (e.g. "October" from an old
                               workbook) can't render in a date input, so it
                               falls back to a text input instead of showing
                               a silently blank picker. */
                            (() => {
                              const val = draftAnswers[q.question_n]?.response || '';
                              const isIso = !val || /^\d{4}-\d{2}-\d{2}$/.test(val);
                              const set = (v) => setDraftAnswers(prev => ({
                                ...prev,
                                [q.question_n]: { ...prev[q.question_n], response: v }
                              }));
                              return isIso ? (
                                <input type="date" min="2000-01-01" max="2100-12-31"
                                  style={{ ...styles.input, fontSize:13, width:200 }}
                                  value={val}
                                  onChange={e => set(e.target.value)}
                                />
                              ) : (
                                <div>
                                  <input type="text"
                                    style={{ ...styles.input, fontSize:13, width:200 }}
                                    value={val}
                                    onChange={e => set(e.target.value)}
                                  />
                                  <div style={{ fontSize:10, color:'#B45309', marginTop:2 }}>
                                    Not a calendar date — clear it to get the date picker back.
                                  </div>
                                </div>
                              );
                            })()
                          ) : q.question_n === 7 ? (
                            /* Q7 MSA — textarea + file attachment */
                            <div>
                              <textarea
                                style={{ ...styles.input, minHeight:56, fontSize:13, resize:'vertical' }}
                                value={draftAnswers[q.question_n]?.response || ''}
                                onChange={e => setDraftAnswers(prev => ({
                                  ...prev,
                                  [q.question_n]: { ...prev[q.question_n], response: e.target.value }
                                }))}
                                placeholder="Notes about MSA or pricing terms…"
                              />
                              <MsaAttachment requestId={current.id} msa_filename={current.msa_filename}
                                readOnly={isReadOnly} onUploaded={() => reload(current.id)} />
                            </div>
                          ) : (
                            <textarea
                              style={{ ...styles.input, minHeight:56, fontSize:13, resize:'vertical' }}
                              value={draftAnswers[q.question_n]?.response || ''}
                              onChange={e => setDraftAnswers(prev => ({
                                ...prev,
                                [q.question_n]: { ...prev[q.question_n], response: e.target.value }
                              }))}
                              placeholder="Enter response…"
                            />
                          )}
                          {/* Follow-up field for select questions */}
                          {!isReadOnly && (
                            <input
                              style={{ ...styles.input, fontSize:12, marginTop:4 }}
                              value={draftAnswers[q.question_n]?.follow_up || ''}
                              onChange={e => setDraftAnswers(prev => ({
                                ...prev,
                                [q.question_n]: { ...prev[q.question_n], follow_up: e.target.value }
                              }))}
                              placeholder="Follow-up / clarification (optional)"
                            />
                          )}
                          {isReadOnly && current.answers?.find(a=>a.question_n===q.question_n)?.follow_up && (
                            <div style={{ fontSize:12, color:'#6B7280', marginTop:4, fontStyle:'italic' }}>
                              {current.answers.find(a=>a.question_n===q.question_n).follow_up}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
                {!isReadOnly && visibleQs.length > 0 && (
                  <div style={{ borderTop:'1px solid #E5E7EB', paddingTop:12, display:'flex', justifyContent:'flex-end' }}>
                    <button style={styles.btn} onClick={saveAnswers} disabled={saving}>💾 Save Answers</button>
                  </div>
                )}
              </div>
            )}

            {/* Labor */}
            {section === 'labor' && (
              <div style={styles.card}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                  <h2 style={{ ...styles.h2, margin:0 }}>
                    {current.msa_filename ? 'MSA Negotiated Pay Rates' : 'Labor — Target Rates'}
                  </h2>
                  {!isReadOnly && (
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      {msaLaborLines.length > 0 && (
                        <select style={{ ...styles.input, width:220, fontSize:12 }} value=""
                          disabled={saving}
                          title="Adds a labor line pre-filled with the confirmed MSA rates"
                          onChange={e => {
                            const line = msaLaborLines.find(l => l.id === Number(e.target.value));
                            if (line) addLaborFromMsa(line);
                            e.target.value = '';
                          }}>
                          <option value="">＄ From MSA…</option>
                          {msaLaborLines.map(l => (
                            <option key={l.id} value={l.id}>
                              {l.craft || l.msa_description}{l.bill_st != null ? ` ($${l.bill_st}/hr)` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                      <button style={styles.btn} onClick={addLabor} disabled={saving}>+ Add Labor</button>
                    </div>
                  )}
                </div>
                <datalist id="req-craft-options">
                  {crafts.map(c => <option key={c.id} value={c.name} />)}
                </datalist>
                <div style={{ background:'#FEF3C7', border:'1px solid #FBBF24', borderRadius:6,
                  padding:'8px 12px', marginBottom:10, fontSize:12, color:'#92400E' }}>
                  <strong>PAY = what SSS pays the employee (wages). BILL = what the customer is charged.</strong><br/>
                  <strong>Bill rates:</strong> if the ST and OT bill rates are known, enter them — otherwise leave blank. Same with Pay rates.
                  <strong> Set Pricing</strong> should have known ST/OT bill rates and most have set pay rates — enter them.
                  Use the exact <strong>MSA or Set Rates wording</strong> for the craft so it matches the contract.
                  Leadership sets final pricing on the estimate.<br/>
                  <strong>Every line needs at least one phase</strong> (Phases button → dates + headcount) before you can submit — that's what drives the hours.
                  Exception: <strong>RATE ONLY</strong> lines — check it when the customer just wants a rate on the quote for a position we're not staffing (no phases, no hours, no revenue).<br/>
                  <strong>Per diem travel days</strong> default to 2 (mob-in day before start + mob-out day after end, hotels/food) — adjust per line if needed.
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={styles.table}>
                    <thead><tr>
                      <th style={styles.th}>{current.msa_filename ? 'MSA Craft Name' : 'Craft'}</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Qty</th>
                      <th style={styles.th} title="Hourly wage SSS pays the employee — NOT the customer rate">Pay ST<br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>we pay</span></th>
                      <th style={styles.th} title="Hourly OT wage SSS pays the employee">Pay OT<br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>we pay</span></th>
                      <th style={{...styles.th, color:'#1E40AF'}} title="Hourly rate charged to the customer — MSA/contract rates go here">Bill ST<br/><span style={{fontWeight:400,fontSize:9,color:'#60A5FA'}}>customer pays</span></th>
                      <th style={{...styles.th, color:'#1E40AF'}} title="Hourly OT rate charged to the customer">Bill OT<br/><span style={{fontWeight:400,fontSize:9,color:'#60A5FA'}}>customer pays</span></th>
                      <th style={styles.th}>Per Diem</th>
                      <th style={styles.th} title="Quote the rate only — no hours. 'We're not quoting this position, but if you want them this is what they cost.' Exempt from the phase requirement.">Rate<br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>only</span></th>
                      <th style={styles.th}></th>
                    </tr></thead>
                    <tbody>
                      {(current.labor_lines || []).map(line => (
                        <>
                          <tr key={line.id}>
                            <td style={styles.td}>
                              {isReadOnly ? (
                                <div>
                                  {line.craft}
                                  {line.msa_description && (
                                    <div style={{ fontSize:10, color:'#6B7280' }}>{line.msa_description}</div>
                                  )}
                                </div>
                              ) : (() => {
                                const m = line.target_bill_st == null ? msaFuzzyMatch(line.craft, msaLaborLines) : null;
                                return (
                                  <div>
                                    <input style={{ ...styles.input, width:130, fontSize:12 }}
                                      list="req-craft-options"
                                      placeholder="Search crafts…"
                                      title="Search the Crafts list (Operations → Crafts) or type a custom craft"
                                      defaultValue={line.craft}
                                      onBlur={e => updateLabor(line.id, { craft: e.target.value })} />
                                    <input style={{ ...styles.input, width:130, fontSize:11, marginTop:3,
                                        ...(msaWordingRequired && !line.msa_description ? { borderColor:'#DC2626' } : {}) }}
                                      placeholder={msaWordingRequired ? 'MSA wording (required)…' : 'MSA / Set Pricing wording…'}
                                      title="The exact craft wording from the MSA or Set Pricing sheet — carries to the estimate so the quote matches the contract"
                                      defaultValue={line.msa_description || ''}
                                      onBlur={e => updateLabor(line.id, { msa_description: e.target.value.trim() || null })} />
                                    {m && (
                                      <div
                                        title={`Matches MSA line "${m.msa_description}" — click to apply the MSA rates`}
                                        onClick={() => updateLabor(line.id, {
                                          target_pay_st: m.pay_st != null ? Number(m.pay_st) : undefined,
                                          target_pay_ot: m.pay_ot != null ? Number(m.pay_ot) : undefined,
                                          target_bill_st: m.bill_st != null ? Number(m.bill_st) : undefined,
                                          target_bill_ot: m.bill_ot != null ? Number(m.bill_ot) : undefined,
                                        })}
                                        style={{ marginTop:3, fontSize:10, fontWeight:600, color:'#1E40AF',
                                          background:'#DBEAFE', padding:'2px 6px', borderRadius:8, cursor:'pointer',
                                          display:'inline-block' }}>
                                        ＄ MSA{m.bill_st != null ? ` $${m.bill_st}/hr` : ''} — apply
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td style={styles.td}>
                              {isReadOnly ? line.labor_type : (
                                <select style={{ ...styles.input, width:110, fontSize:12 }}
                                  value={line.labor_type}
                                  onChange={e => updateLabor(line.id, { labor_type: e.target.value })}>
                                  {LABOR_TYPES.map(t => <option key={t}>{t}</option>)}
                                </select>
                              )}
                            </td>
                            <td style={styles.td}>
                              {isReadOnly ? positionsFor(line) : (
                                !line.rate_only && peakHeadcount(line.phases) > 0 ? (
                                  // Auto-derived from the phases (peak day+night headcount) — the
                                  // same people whose hours the phases already book.
                                  <span style={{ fontSize:12, fontWeight:700, display:'inline-block', width:50, textAlign:'center' }}
                                    title={`Auto from phase headcount (peak day + night = ${positionsFor(line)}). Change the headcount in Phases to change positions.`}>
                                    {positionsFor(line)}
                                  </span>
                                ) : (
                                  <input type="number" min="1" style={{ ...styles.input, width:50, fontSize:12 }}
                                    title="No staffed phases on this line — positions are typed here. Once phases carry headcount, this auto-derives from them."
                                    defaultValue={line.qty_positions}
                                    onBlur={e => updateLabor(line.id, { qty_positions: Number(e.target.value) })} />
                                )
                              )}
                            </td>
                            <td style={styles.td}>
                              {isReadOnly ? (line.target_pay_st!=null ? <span style={{ color:'#DC2626' }}>{fmtMoney(line.target_pay_st)}</span> : '—') : (
                                <input type="number" step="0.01" style={{ ...styles.input, width:80, fontSize:12, color:'#DC2626', fontWeight:600 }}
                                  defaultValue={line.target_pay_st || ''}
                                  onBlur={e => {
                                    const st = Number(e.target.value);
                                    const body = { target_pay_st: st };
                                    // 1.5× by federal law — except subcontractors,
                                    // whose rates come from the sub agreement.
                                    if (line.labor_type !== 'Subcontractor') body.target_pay_ot = parseFloat((st * 1.5).toFixed(2));
                                    updateLabor(line.id, body);
                                  }} />
                              )}
                            </td>
                            <td style={styles.td}>
                              {line.labor_type === 'Subcontractor' ? (
                                isReadOnly ? (line.target_pay_ot!=null ? <span style={{ color:'#DC2626' }}>{fmtMoney(line.target_pay_ot)}</span> : '—') : (
                                  <input type="number" step="0.01" style={{ ...styles.input, width:80, fontSize:12, color:'#DC2626', fontWeight:600 }}
                                    key={`tpo-${line.id}-${line.target_pay_ot}`}
                                    defaultValue={line.target_pay_ot || ''}
                                    title="Subcontractor OT rate per the sub agreement"
                                    onBlur={e => updateLabor(line.id, { target_pay_ot: Number(e.target.value) })} />
                                )
                              ) : (
                                <span style={{ fontSize:12, color:'#DC2626' }}
                                  title="Always 1.5× Pay ST (federal OT law) — derived, not editable">
                                  {line.target_pay_st != null ? fmtMoney(Number(line.target_pay_st) * 1.5) : '—'}
                                </span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {isReadOnly ? (line.target_bill_st!=null ? fmtMoney(line.target_bill_st) : '—') : (
                                <input type="number" step="0.01" style={{ ...styles.input, width:80, fontSize:12 }}
                                  defaultValue={line.target_bill_st || ''}
                                  onBlur={e => updateLabor(line.id, { target_bill_st: Number(e.target.value) })} />
                              )}
                            </td>
                            <td style={styles.td}>
                              {isReadOnly ? (line.target_bill_ot!=null ? fmtMoney(line.target_bill_ot) : '—') : (
                                <input type="number" step="0.01" style={{ ...styles.input, width:80, fontSize:12 }}
                                  defaultValue={line.target_bill_ot || ''}
                                  onBlur={e => updateLabor(line.id, { target_bill_ot: Number(e.target.value) })} />
                              )}
                            </td>
                            <td style={styles.td}>
                              {isReadOnly
                                ? (line.per_diem_eligible
                                    ? `✓${(line.travel_days ?? 0) > 0 ? ` +${line.travel_days} travel` : ''}`
                                    : '—')
                                : (
                                  <>
                                    <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
                                      <input type="checkbox" checked={!!line.per_diem_eligible}
                                        onChange={e => updateLabor(line.id, { per_diem_eligible: e.target.checked })} />
                                      Eligible
                                    </label>
                                    {line.per_diem_eligible && (
                                      <>
                                        <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6B7280', marginTop:3 }}
                                          title="Extra per diem days per person for mob-in/mob-out — SSS standard is 2 (day before start + day after end, hotels/food)">
                                          + <input type="number" min="0" style={{ ...styles.input, width:42, fontSize:11, padding:'2px 4px' }}
                                            defaultValue={line.travel_days ?? 2}
                                            onBlur={e => updateLabor(line.id, { travel_days: Math.max(0, Math.floor(Number(e.target.value)||0)) })} />
                                          travel days
                                        </label>
                                        <select style={{ ...styles.input, fontSize:10, padding:'2px 4px', marginTop:3, width:110 }}
                                          title="7 days/wk (SSS standard): per diem pays every calendar day of the phase span — weekends and shift-rollover days included. Work days only: pays only days hours book."
                                          value={line.per_diem_mode || 'calendar'}
                                          onChange={e => updateLabor(line.id, { per_diem_mode: e.target.value })}>
                                          <option value="calendar">PD 7 days/wk</option>
                                          <option value="work_days">PD work days</option>
                                        </select>
                                        <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6B7280', marginTop:3 }}
                                          title="Per diem rate per day if known (MSA / contract — e.g. 135). Leave blank if unknown; leadership sets it on the estimate.">
                                          $ <input type="number" min="0" step="0.01" style={{ ...styles.input, width:56, fontSize:11, padding:'2px 4px' }}
                                            defaultValue={line.target_per_diem_bill ?? ''}
                                            onBlur={e => updateLabor(line.id, { target_per_diem_bill: e.target.value === '' ? null : Number(e.target.value) })} />
                                          /day
                                        </label>
                                      </>
                                    )}
                                  </>
                                )}
                            </td>
                            <td style={{ ...styles.td, textAlign:'center' }}>
                              {isReadOnly
                                ? (line.rate_only ? '✓' : '—')
                                : (
                                  <input type="checkbox" checked={!!line.rate_only}
                                    title="Quote rate only — no hours; exempt from the phase requirement"
                                    onChange={e => updateLabor(line.id, { rate_only: e.target.checked })} />
                                )}
                            </td>
                            <td style={styles.td}>
                              {(() => {
                                const filledPhases = (line.phases||[]).filter(ph =>
                                  ph.start_date && ph.end_date &&
                                  ((Number(ph.day_hc)||0) + (Number(ph.night_hc)||0)) > 0);
                                const filled = filledPhases.length;
                                const hasDay = filledPhases.some(ph => (Number(ph.day_hc)||0) > 0);
                                const hasNight = filledPhases.some(ph => (Number(ph.night_hc)||0) > 0);
                                const shiftChip = hasDay && hasNight ? '☀️/🌙 Day+Night'
                                  : hasDay ? '☀️ Day' : hasNight ? '🌙 Night' : null;
                                return (<>
                                  {shiftChip && (
                                    <div style={{ fontSize:10, fontWeight:700, marginBottom:3, textAlign:'center',
                                      color: hasNight && !hasDay ? '#4338CA' : '#B45309' }}>
                                      {shiftChip}
                                    </div>
                                  )}
                                  {!isReadOnly && (
                                    <>
                                      <button
                                        title={filled
                                          ? `${filled} phase${filled>1?'s':''} entered`
                                          : line.rate_only ? 'Rate only — no phases needed' : 'No phases yet — required before submit'}
                                        style={{ ...styles.btnSecondary, fontSize:11, padding:'3px 8px',
                                          ...(filled
                                            ? { background:'#ECFDF5', border:'1px solid #10B981', color:'#047857', fontWeight:700 }
                                            : line.rate_only ? {} : { border:'1px solid #F59E0B', background:'#FFFBEB', color:'#B45309' }) }}
                                        onClick={() => setExpandedPhase(expandedPhase===line.id ? null : line.id)}>
                                        {expandedPhase===line.id ? 'Close'
                                          : filled ? `✓ Phases (${filled})` : 'Phases'}
                                      </button>
                                      {' '}
                                      <button style={{ ...styles.btnDanger, fontSize:11, padding:'3px 8px' }}
                                        onClick={() => deleteLabor(line.id)}>Del</button>
                                    </>
                                  )}
                                  {isReadOnly && (
                                    <button style={{ ...styles.btnSecondary, fontSize:11, padding:'3px 8px' }}
                                      onClick={() => setExpandedPhase(expandedPhase===line.id ? null : line.id)}>
                                      {filled ? `✓ Phases (${filled})` : 'Phases'}
                                    </button>
                                  )}
                                </>);
                              })()}
                            </td>
                          </tr>
                          {expandedPhase === line.id && (
                            <tr key={`ph-${line.id}`}>
                              <td colSpan={10} style={{ background:'#F8FAFC', padding:12 }}>
                                <RequestPhaseEditor
                                  phases={line.phases || []}
                                  readOnly={isReadOnly}
                                  onSave={phases => savePhases(line.id, phases)}
                                />
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                      {(current.labor_lines||[]).length > 0 && (() => {
                        let st = 0, ot = 0, pdDays = 0;
                        for (const l of current.labor_lines) {
                          if (l.rate_only) continue;
                          const t = phaseHoursPreview(l.phases || [], 'weekly_over_40');
                          st += t.st; ot += t.ot;
                          if (l.per_diem_eligible) {
                            const basis = l.per_diem_mode === 'work_days' ? t.workDays : (t.calendarDays || t.workDays);
                            pdDays += (basis + Math.max(0, Math.floor(Number(l.travel_days) || 0))) * positionsFor(l);
                          }
                        }
                        return (
                          <tr style={{ borderTop:'2px solid #1F2937' }}>
                            <td colSpan={10} style={{ ...styles.td, fontWeight:700, textAlign:'right' }}>
                              Total hours: {st.toLocaleString()} ST / {ot.toLocaleString()} OT
                              <span style={{ fontWeight:400, color:'#9CA3AF' }}> · {(st+ot).toLocaleString()} total · assumes weekly &gt;40 OT</span>
                              <span style={{ marginLeft:14 }}>Per diem days: {pdDays.toLocaleString()}</span>
                              <span style={{ fontWeight:400, color:'#9CA3AF' }}> · incl. travel days × headcount</span>
                            </td>
                          </tr>
                        );
                      })()}
                      {(current.labor_lines||[]).length === 0 && (
                        <tr><td colSpan={10} style={{ ...styles.td, textAlign:'center', color:'#9CA3AF' }}>
                          No labor lines.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Equipment */}
            {section === 'equipment' && (
              <div style={styles.card}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, flexWrap:'wrap', gap:8 }}>
                  <h2 style={{ ...styles.h2, margin:0 }}>Equipment — Target Usage Rate</h2>
                  {!isReadOnly && (
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      {msaEquipLines.length > 0 && (
                        <select style={{ ...styles.input, width:220, fontSize:12 }} value=""
                          disabled={saving}
                          title="Adds an equipment line pre-filled with the confirmed MSA rate"
                          onChange={e => {
                            const line = msaEquipLines.find(l => l.id === Number(e.target.value));
                            if (line) addEquipFromMsa(line);
                            e.target.value = '';
                          }}>
                          <option value="">＄ From MSA…</option>
                          {msaEquipLines.map(l => (
                            <option key={l.id} value={l.id}>
                              {l.msa_description}{l.day_rate != null ? ` ($${l.day_rate}/day)` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                      <input style={{ ...styles.input, width:300, fontSize:14, fontWeight:600,
                          border:`2px solid ${ORANGE}`, padding:'9px 12px' }}
                        placeholder="🔍 Search equipment catalog…"
                        title="Primary way to add equipment — pulls the CAT-CLASS so the line matches Rental Man stock downstream"
                        value={catalogQ}
                        onChange={e => setCatalogQ(e.target.value)} />
                      <button style={{ ...styles.btnSecondary, fontSize:11, padding:'5px 10px', whiteSpace:'nowrap' }}
                        title="Only for items not in the catalog — free-text description, no CAT-CLASS"
                        onClick={addEquipManual} disabled={saving}>+ Add Non-Inventory Equipment</button>
                    </div>
                  )}
                </div>
                {!isReadOnly && (
                  <div style={{ fontSize:11, color:'#6B7280', marginBottom:8 }}>
                    Search the catalog first — it carries the CAT-CLASS that follows the line to Inventory and the Equipment Gantt. Use <strong>Add Non-Inventory Equipment</strong> only for items not in the catalog.
                  </div>
                )}
                {!isReadOnly && catalogQ && equipCatalog.length > 0 && (
                  <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:6, maxHeight:180, overflowY:'auto', marginBottom:10 }}>
                    {equipCatalog.slice(0,30).map(item => (
                      <div key={item.id}
                        onClick={() => addEquipFromCatalog(item)}
                        style={{ padding:'6px 10px', cursor:'pointer', fontSize:12, borderBottom:'1px solid #F3F4F6' }}
                        onMouseEnter={e => e.currentTarget.style.background='#FFF7ED'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <strong>{item.description}</strong> <span style={{color:'#9CA3AF'}}>{item.category}</span>
                        {item.cat_class && <span style={{ marginLeft:6, fontSize:10, fontWeight:600, color:'#374151', background:'#E5E7EB', padding:'1px 5px', borderRadius:6 }}>{item.cat_class}</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ background:'#FEF3C7', border:'1px solid #FBBF24', borderRadius:6,
                  padding:'8px 12px', marginBottom:10, fontSize:12, color:'#92400E' }}>
                  <strong>Rates:</strong> if the customer has <strong>MSA or Set Pricing</strong>, enter that pricing and use the exact MSA / Set Pricing wording
                  (MSA Description column). If neither, enter what you think we can bill for the item. Unknown — leave the rate blank.
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={styles.table}>
                    <thead><tr>
                      <th style={styles.th}>Description</th>
                      <th style={styles.th} title="Rental Man CAT-CLASS (e.g. 350-14). Follows the line to the estimate and the Equipment Gantt stock match.">CAT-CLASS</th>
                      <th style={styles.th}>MSA Description</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Qty</th>
                      <th style={styles.th} title="On-site start — sets this line's window for the estimate / Equipment Gantt">Start</th>
                      <th style={styles.th} title="On-site end — Days auto-fills from Start → End (inclusive)">End</th>
                      <th style={styles.th} title="Auto-set from Start → End when both are picked; otherwise enter manually">Days</th>
                      <th style={styles.th}>Target Usage Rate</th>
                      <th style={styles.th} title="Qty × Days × Target Usage Rate">Total</th>
                      {!isReadOnly && <th style={styles.th}></th>}
                    </tr></thead>
                    <tbody>
                      {(current.equipment_lines||[]).map(line => {
                        // Pick Start + End → Days fills; type Days → End fills (inclusive).
                        const setDates = (patch) => {
                          const s = patch.start_date !== undefined ? patch.start_date : (line.start_date ? String(line.start_date).slice(0,10) : null);
                          const en = patch.end_date !== undefined ? patch.end_date : (line.end_date ? String(line.end_date).slice(0,10) : null);
                          if (s && en) { const span = Math.floor((new Date(en) - new Date(s)) / 86400000) + 1; if (span > 0) patch.num_days = span; }
                          return updateEquip(line.id, patch);
                        };
                        return (
                        <tr key={line.id}>
                          <td style={styles.td}>
                            {isReadOnly ? line.description : (() => {
                              const m = line.target_day_rate == null ? msaFuzzyMatch(line.description, msaEquipLines) : null;
                              return (
                                <div>
                                  <input style={{ ...styles.input, width:180, fontSize:12 }}
                                    defaultValue={line.description}
                                    onBlur={e => updateEquip(line.id, { description: e.target.value })} />
                                  {m && (
                                    <div
                                      title={`Matches MSA line "${m.msa_description}" — click to apply the MSA rate`}
                                      onClick={() => updateEquip(line.id, {
                                        target_day_rate: m.day_rate != null ? Number(m.day_rate) : undefined,
                                        msa_description: m.msa_description,
                                      })}
                                      style={{ marginTop:3, fontSize:10, fontWeight:600, color:'#1E40AF',
                                        background:'#DBEAFE', padding:'2px 6px', borderRadius:8, cursor:'pointer',
                                        display:'inline-block' }}>
                                      ＄ MSA{m.day_rate != null ? ` $${m.day_rate}/day` : ''} — apply
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly
                              ? (line.cat_class || <span style={{ fontSize:10, fontWeight:700, color:'#B45309', background:'#FFFBEB', border:'1px solid #F59E0B', padding:'1px 5px', borderRadius:6 }}>⚠ none</span>)
                              : (
                              <div>
                                <input style={{ ...styles.input, width:80, fontSize:12,
                                    ...(!line.cat_class ? { border:'1px solid #F59E0B', background:'#FFFBEB' } : {}) }}
                                  placeholder="e.g. 350-14"
                                  defaultValue={line.cat_class || ''}
                                  onBlur={e => updateEquip(line.id, { cat_class: e.target.value.trim() || null })} />
                                {!line.cat_class && (
                                  <div title="CAT-CLASS is the join key that follows this line to Inventory and field-services — lines without one can't be matched to Rental Man stock"
                                    style={{ fontSize:10, fontWeight:700, color:'#B45309', marginTop:2 }}>⚠ no CAT-CLASS</div>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly
                              ? (line.msa_description || <span style={{color:'#9CA3AF'}}>--</span>)
                              : (
                              <input style={{ ...styles.input, width:180, fontSize:12,
                                  ...(msaWordingRequired && !line.msa_description ? { borderColor:'#DC2626' } : {}) }}
                                placeholder={msaWordingRequired ? 'MSA wording (required)…' : 'Quote description…'}
                                defaultValue={line.msa_description || ''}
                                onBlur={e => updateEquip(line.id, { msa_description: e.target.value || null })} />
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? line.equip_type : (
                              <select style={{ ...styles.input, width:130, fontSize:12 }}
                                value={line.equip_type}
                                onChange={e => updateEquip(line.id, { equip_type: e.target.value })}>
                                {EQUIP_TYPES.map(t => <option key={t}>{t}</option>)}
                              </select>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? line.qty : (
                              <input type="number" min="0" style={{ ...styles.input, width:60, fontSize:12 }}
                                defaultValue={line.qty}
                                onBlur={e => updateEquip(line.id, { qty: Number(e.target.value) })} />
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly
                              ? (line.start_date ? fmtDate(line.start_date) : '—')
                              : <BlurDateInput style={{ ...styles.input, width:120, fontSize:11 }}
                                  value={line.start_date}
                                  onCommit={v => setDates({ start_date: v })} />}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly
                              ? (line.end_date ? fmtDate(line.end_date) : '—')
                              : (<>
                                  <BlurDateInput style={{ ...styles.input, width:120, fontSize:11 }}
                                    min={line.start_date ? String(line.start_date).slice(0,10) : undefined}
                                    value={line.end_date}
                                    onCommit={v => setDates({ end_date: v })} />
                                  {line.start_date && line.end_date && (
                                    <button title="Carry these dates (and Days) down to every equipment line"
                                      style={{ ...styles.btnSecondary, fontSize:10, padding:'2px 5px', marginLeft:3 }}
                                      onClick={() => applyEquipDatesToAll(line)}>⤓ all</button>
                                  )}
                                  {line.start_date && line.end_date && String(line.end_date).slice(0,10) < String(line.start_date).slice(0,10) && (
                                    <div style={{ color:'#B91C1C', fontSize:10, fontWeight:700 }}>ends before start</div>
                                  )}
                                </>)}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? line.num_days : (
                              <input type="number" min="0" style={{ ...styles.input, width:60, fontSize:12 }}
                                key={`d-${line.id}-${line.num_days}`}
                                defaultValue={line.num_days}
                                title="Enter # of days to fill End from Start (inclusive)"
                                onBlur={e => {
                                  const days = Number(e.target.value) || 0;
                                  const s = line.start_date ? String(line.start_date).slice(0,10) : '';
                                  if (s && days > 0) setDates({ end_date: endFromDays(s, days) });
                                  else updateEquip(line.id, { num_days: days });
                                }} />
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? (line.target_day_rate!=null ? fmtMoney(line.target_day_rate) : '--') : (
                              <label style={{ display:'inline-flex', alignItems:'center', gap:2, fontSize:12, color:'#6B7280' }}>$
                              <input type="number" step="0.01" style={{ ...styles.input, width:90, fontSize:12 }}
                                defaultValue={line.target_day_rate || ''}
                                onBlur={e => updateEquip(line.id, { target_day_rate: Number(e.target.value) })} />
                              </label>
                            )}
                          </td>
                          <td style={{ ...styles.td, fontWeight:600, fontSize:12 }}>
                            {fmtMoney((Number(line.qty)||0) * (Number(line.num_days)||0) * (Number(line.target_day_rate)||0))}
                          </td>
                          {!isReadOnly && (
                            <td style={styles.td}>
                              <button style={{ ...styles.btnSecondary, fontSize:11, padding:'3px 8px', marginRight:4 }}
                                title="Duplicate this line (same description / CAT-CLASS / rate) — set a new qty + dates"
                                onClick={() => dupEquip(line)}>Dup</button>
                              <button style={{ ...styles.btnDanger, fontSize:11, padding:'3px 8px' }}
                                onClick={() => deleteEquip(line.id)}>Del</button>
                            </td>
                          )}
                        </tr>
                        );
                      })}
                      {(current.equipment_lines||[]).length > 0 && (() => {
                        const lineTotal = l => (Number(l.qty)||0) * (Number(l.num_days)||0) * (Number(l.target_day_rate)||0);
                        const byType = {};
                        for (const l of current.equipment_lines) {
                          const t = l.equip_type || 'Utilization';
                          byType[t] = (byType[t]||0) + lineTotal(l);
                        }
                        return (<>
                          {EQUIP_TYPES.filter(t => byType[t] != null).map(t => (
                            <tr key={`et-${t}`} style={{ background:'#F9FAFB' }}>
                              <td colSpan={9} style={{ ...styles.td, fontWeight:600, textAlign:'right', color:'#6B7280' }}>{t} total</td>
                              <td style={{ ...styles.td, fontWeight:600, color:'#6B7280' }}>{fmtMoney(byType[t])}</td>
                              {!isReadOnly && <td style={styles.td}></td>}
                            </tr>
                          ))}
                          <tr style={{ borderTop:'2px solid #1F2937' }}>
                            <td colSpan={9} style={{ ...styles.td, fontWeight:700, textAlign:'right' }}>Tab total</td>
                            <td style={{ ...styles.td, fontWeight:700 }}>
                              {fmtMoney(Object.values(byType).reduce((s,v) => s+v, 0))}
                            </td>
                            {!isReadOnly && <td style={styles.td}></td>}
                          </tr>
                        </>);
                      })()}
                      {(current.equipment_lines||[]).length === 0 && (
                        <tr><td colSpan={11} style={{ ...styles.td, textAlign:'center', color:'#9CA3AF' }}>
                          {isReadOnly ? 'No equipment.' : 'Search the catalog to add equipment, or use Add Non-Inventory Equipment for items not in the catalog.'}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Consumables */}
            {section === 'consumables' && (
              <div style={styles.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:10 }}>
                  <h2 style={{ ...styles.h2, margin:0 }}>Consumables, Services and Charges — Target Bill Rate</h2>
                  {!isReadOnly && (
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <label style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:12, color:'#6B7280' }}
                        title="Default markup over cost for the 'Apply to all' button. Target Bill Rate = Cost × (1 + this).">
                        Default markup
                        <input type="number" step="0.1" min="0" key={`cm-${current.id}-${consMarkup}`}
                          style={{ ...styles.input, width:64, fontSize:12, fontWeight:700, color:'#047857' }}
                          defaultValue={Math.round(consMarkup * 1000) / 10}
                          onBlur={e => {
                            if (e.target.value === '') return;
                            const m = Number(e.target.value) / 100;
                            if (isNaN(m) || m < 0 || m >= 100) return;
                            if (Math.abs(m - consMarkup) < 0.00005) return;
                            updateHeader({ consumable_markup_pct: Math.round(m * 10000) / 10000 });
                          }} />
                        %
                      </label>
                      <button style={styles.btnSecondary} onClick={applyConsMarkup} disabled={saving}
                        title="Set Target Bill Rate = Cost × (1 + default markup) on every line that has a cost.">Apply to all lines</button>
                      <button style={styles.btn} onClick={addCons} disabled={saving}>+ Add</button>
                    </div>
                  )}
                </div>
                <div style={{ background:'#FEF3C7', border:'1px solid #FBBF24', borderRadius:6,
                  padding:'8px 12px', marginBottom:10, fontSize:12, color:'#92400E' }}>
                  <strong>Same rules as equipment:</strong> MSA / Set Pricing — enter that pricing with the exact contract or manufacturer wording.
                  Otherwise enter <strong>our cost and the markup %</strong> (the bill rate calculates as cost × (1 + markup)). Unknown — leave blank.
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={styles.table}>
                    <thead><tr>
                      <th style={styles.th}>Description</th>
                      <th style={styles.th}>Part #</th>
                      <th style={styles.th} title="Tags the line for later grouping — does not change the quote.">Category</th>
                      <th style={styles.th}>Qty</th>
                      <th style={styles.th}>Unit</th>
                      <th style={styles.th} title="What it costs us per unit (from the supplier quote). Carries to the estimate.">Cost</th>
                      <th style={styles.th}>Target Bill Rate</th>
                      <th style={styles.th} title="Markup over cost — entering a % sets Target Bill Rate to Cost × (1 + markup).">Markup %</th>
                      <th style={styles.th} title="Qty × Target Bill Rate">Total</th>
                      {!isReadOnly && <th style={styles.th}></th>}
                    </tr></thead>
                    <tbody>
                      {(current.consumable_lines||[]).map(line => {
                        const cost = Number(line.target_cost);
                        const bill = Number(line.target_bill_rate);
                        const markup = cost > 0 && bill > 0 ? (bill - cost) / cost : null;
                        return (
                        <tr key={line.id}>
                          <td style={styles.td}>
                            {isReadOnly ? line.description : (
                              <input style={{ ...styles.input, width:200, fontSize:12 }}
                                defaultValue={line.description}
                                onBlur={e => updateCons(line.id, { description: e.target.value })} />
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? (line.part_number||'—') : (
                              <input style={{ ...styles.input, width:90, fontSize:12 }}
                                placeholder="part #"
                                defaultValue={line.part_number || ''}
                                onBlur={e => updateCons(line.id, { part_number: e.target.value.trim() || null })} />
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? (line.category||'Consumable') : (
                              <select style={{ ...styles.input, width:110, fontSize:12 }}
                                value={line.category || 'Consumable'}
                                onChange={e => updateCons(line.id, { category: e.target.value })}>
                                {CONS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? Math.round(Number(line.qty)||0) : (
                              <input type="number" min="0" step="1" style={{ ...styles.input, width:70, fontSize:12 }}
                                key={`q-${line.id}-${line.qty}`}
                                defaultValue={Math.round(Number(line.qty)||0)}
                                onBlur={e => updateCons(line.id, { qty: Math.max(0, Math.round(Number(e.target.value)||0)) })} />
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? (line.unit||'—') : (
                              <input style={{ ...styles.input, width:70, fontSize:12 }}
                                defaultValue={line.unit || ''}
                                onBlur={e => updateCons(line.id, { unit: e.target.value })} />
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? (line.target_cost!=null ? <span style={{ color:'#DC2626' }}>{fmtMoney(line.target_cost)}</span> : '—') : (
                              <label style={{ display:'inline-flex', alignItems:'center', gap:2, fontSize:12, color:'#DC2626' }}>$
                              <input type="number" step="0.01" key={`cc-${line.id}-${line.target_cost}`}
                                style={{ ...styles.input, width:80, fontSize:12, color:'#DC2626', fontWeight:600 }}
                                defaultValue={line.target_cost ?? ''}
                                title="Changing the cost re-applies the line's markup (or the default markup) and recalculates the bill rate"
                                onBlur={e => {
                                  const v = e.target.value === '' ? null : Number(e.target.value);
                                  const body = { target_cost: v };
                                  // Hold the line's markup (or apply the default): new bill = new cost × (1 + markup)
                                  if (v > 0) {
                                    const eff = markup != null ? markup : consMarkup;
                                    body.target_bill_rate = Math.round((v * (1 + eff)) * 100) / 100;
                                  }
                                  updateCons(line.id, body);
                                }} />
                              </label>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? (line.target_bill_rate!=null ? fmtMoney(line.target_bill_rate) : '—') : (
                              <label style={{ display:'inline-flex', alignItems:'center', gap:2, fontSize:12, color:'#6B7280' }}>$
                              <input type="number" step="0.01" key={`br-${line.id}-${line.target_bill_rate}`}
                                style={{ ...styles.input, width:90, fontSize:12 }}
                                defaultValue={line.target_bill_rate || ''}
                                onBlur={e => updateCons(line.id, { target_bill_rate: Number(e.target.value) })} />
                              </label>
                            )}
                          </td>
                          <td style={styles.td}>
                            {isReadOnly ? (markup != null ? `${(markup*100).toFixed(1)}%` : '—') : (
                              <label style={{ display:'inline-flex', alignItems:'center', gap:2, fontSize:12, color:'#6B7280' }}>
                              <input type="number" step="0.1" min="0" key={`mk-${line.id}-${markup}`}
                                style={{ ...styles.input, width:64, fontSize:12, fontWeight:700,
                                  color: markup != null && markup < 0 ? '#EF4444' : '#047857' }}
                                defaultValue={markup != null ? Math.round(markup * 1000) / 10 : ''}
                                title="Markup over cost — sets Target Bill Rate to Cost × (1 + markup). Needs a Cost first."
                                onBlur={e => {
                                  if (e.target.value === '') return;
                                  const m = Number(e.target.value) / 100;
                                  if (isNaN(m) || m < 0 || m >= 100 || !(cost > 0)) return;
                                  const newRate = Math.round((cost * (1 + m)) * 100) / 100;
                                  if (Math.abs(newRate - bill) < 0.005) return;
                                  updateCons(line.id, { target_bill_rate: newRate });
                                }} />
                              %</label>
                            )}
                          </td>
                          <td style={{ ...styles.td, fontWeight:600, fontSize:12 }}>
                            {fmtMoney((Number(line.qty)||0) * (bill||0))}
                          </td>
                          {!isReadOnly && (
                            <td style={styles.td}>
                              <button style={{ ...styles.btnDanger, fontSize:11, padding:'3px 8px' }}
                                onClick={() => deleteCons(line.id)}>Del</button>
                            </td>
                          )}
                        </tr>
                        );
                      })}
                      {(current.consumable_lines||[]).length > 0 && (() => {
                        const lineTotal = l => (Number(l.qty)||0) * (Number(l.target_bill_rate)||0);
                        const byCat = {};
                        for (const l of current.consumable_lines) {
                          const c = l.category || 'Consumable';
                          byCat[c] = (byCat[c]||0) + lineTotal(l);
                        }
                        return (<>
                          {CONS_CATEGORIES.filter(c => byCat[c] != null).map(c => (
                            <tr key={`cat-${c}`} style={{ background:'#F9FAFB' }}>
                              <td colSpan={7} style={{ ...styles.td, fontWeight:600, textAlign:'right', color:'#6B7280' }}>{c} total</td>
                              <td style={{ ...styles.td, fontWeight:600, color:'#6B7280' }}>{fmtMoney(byCat[c])}</td>
                              {!isReadOnly && <td style={styles.td}></td>}
                            </tr>
                          ))}
                          <tr style={{ borderTop:'2px solid #1F2937' }}>
                            <td colSpan={7} style={{ ...styles.td, fontWeight:700, textAlign:'right' }}>Tab total</td>
                            <td style={{ ...styles.td, fontWeight:700 }}>
                              {fmtMoney(Object.values(byCat).reduce((s,v) => s+v, 0))}
                            </td>
                            {!isReadOnly && <td style={styles.td}></td>}
                          </tr>
                        </>);
                      })()}
                      {(current.consumable_lines||[]).length === 0 && (
                        <tr><td colSpan={10} style={{ ...styles.td, textAlign:'center', color:'#9CA3AF' }}>
                          No consumable lines.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* MSA rate sheet — extraction review + AM confirm */}
            {section === 'msa' && (
              <div style={styles.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
                  <h2 style={{ ...styles.h2, margin:0 }}>
                    MSA Rate Sheet {msaSheet ? `— ${msaSheet.customer_name}` : ''}
                    {msaSheet && (
                      <span style={{ marginLeft:8, padding:'2px 10px', borderRadius:10, fontSize:11, fontWeight:700,
                        background:(MSA_STATUS_COLOR[msaSheet.status]||{}).bg, color:(MSA_STATUS_COLOR[msaSheet.status]||{}).fg }}>
                        {msaSheet.status}
                      </span>
                    )}
                  </h2>
                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <button
                      style={{ ...styles.btn, opacity: (current.msa_filename && msaConfigured && !extracting) ? 1 : 0.5 }}
                      disabled={!current.msa_filename || !msaConfigured || extracting}
                      title={!msaConfigured ? 'ANTHROPIC_API_KEY is not set on the server'
                        : !current.msa_filename ? 'Attach the MSA file on Q7 first'
                        : msaSheet ? 'Re-extract creates a fresh Draft sheet' : ''}
                      onClick={extractMsa}>
                      {extracting ? '⏳ Extracting…' : msaSheet ? '⚡ Re-extract Rates' : '⚡ Extract Rates from MSA'}
                    </button>
                    {msaSheet && msaSheet.status === 'Draft' && (
                      <button style={{ ...styles.btn, background:'#1E40AF' }} disabled={saving}
                        title="Confirms the rates and catalog matches are correct — unlocks auto-populate on labor/equipment"
                        onClick={() => doSave(async () => setMsaSheet(await api.updateMsaSheet(msaSheet.id, { status:'Confirmed' })))}>
                        ✅ Confirm Rates
                      </button>
                    )}
                    {msaSheet && msaSheet.status !== 'Draft' && msaSheet.status !== 'Verified' && (
                      <button style={{ ...styles.btnSecondary, fontSize:12 }} disabled={saving}
                        onClick={() => doSave(async () => setMsaSheet(await api.updateMsaSheet(msaSheet.id, { status:'Draft' })))}>
                        ↩ Reopen
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ color:'#9CA3AF', fontSize:11, marginBottom:10 }}>
                  Rates extracted from the attached MSA — customer-level, reused on every request for this customer.
                  Review each line, fix the craft / catalog match where the MSA wording differs from ours, then Confirm.
                  Leadership gives the final Verify during estimating.
                  {msaSheet?.verified_by_name && ` Verified by ${msaSheet.verified_by_name}.`}
                </div>
                {!msaSheet && (
                  <div style={{ color:'#9CA3AF', fontSize:13 }}>
                    No rate sheet yet for this customer. Attach the MSA on Q7, then Extract Rates.
                  </div>
                )}
                {msaSheet && (
                  <MsaSheetEditor
                    sheet={msaSheet}
                    catalog={equipCatalog}
                    readOnly={msaSheet.status === 'Verified' && !isAdmin}
                    saving={saving}
                    onLineUpdate={(lineId, body) => doSave(async () => setMsaSheet(await api.updateMsaLine(lineId, body)))}
                    onLineDelete={(lineId) => { if (!confirm('Delete this MSA line?')) return; doSave(async () => setMsaSheet(await api.deleteMsaLine(lineId))); }}
                    onLineAdd={(body) => doSave(async () => setMsaSheet(await api.addMsaLine(msaSheet.id, body)))}
                  />
                )}
              </div>
            )}

            {/* Customer files */}
            {section === 'files' && (
              <div style={styles.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
                  <h2 style={{ ...styles.h2, margin:0 }}>Customer Files</h2>
                </div>
                <div style={{ color:'#9CA3AF', fontSize:11, marginBottom:8 }}>
                  Anything received from the customer (scope docs, drawings, rate sheets…).
                  Files are shared across every revision of this request{current.project_id ? ' and also appear on the linked opportunity in the Project Tracker and Operations' : ''}.
                </div>
                {files.length > 0 && (
                  <div style={{ color:'#6B7280', fontSize:11, marginBottom:8 }}>
                    ↕ Tip: drag a file straight onto an Outlook email or your desktop to attach it.
                  </div>
                )}
                {files.length === 0 && <div style={{ color:'#9CA3AF', fontSize:13 }}>No files uploaded yet.</div>}
                {files.map(f => (
                  <div key={f.id} {...fileDragProps(f)} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px',
                    background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:6, marginBottom:6, fontSize:13,
                    cursor: f.dl_token ? 'grab' : 'default' }}>
                    <span style={{ flex:1, fontWeight:600 }}>📎 {f.original_name}</span>
                    <span style={{ color:'#9CA3AF', fontSize:11 }}>
                      {f.uploaded_by_name || '—'} · {fmtDate(f.created_at)}
                      {f.size_bytes ? ` · ${(f.size_bytes / 1024).toFixed(0)} KB` : ''}
                    </span>
                    <button style={{ ...styles.btnSecondary, fontSize:11, padding:'3px 10px' }}
                      onClick={() => api.downloadRequestFile(current.id, f.id, f.original_name).catch(e => alert(e.message))}>
                      ⬇ Download
                    </button>
                    {(isAdmin || f.uploaded_by === user.id) && (
                      <button style={{ ...styles.btnDanger, fontSize:11, padding:'3px 8px' }}
                        onClick={() => deleteFile(f)}>Del</button>
                    )}
                  </div>
                ))}
                <label
                  onDragOver={e => { e.preventDefault(); setFileDragOver(true); }}
                  onDragLeave={() => setFileDragOver(false)}
                  onDrop={e => { e.preventDefault(); setFileDragOver(false); uploadFiles(e.dataTransfer.files); }}
                  style={{
                    display:'block', textAlign:'center', cursor: saving ? 'wait' : 'pointer',
                    marginTop:8, padding:'16px 10px', borderRadius:6,
                    border:`2px dashed ${fileDragOver ? '#E36F2C' : '#D1D5DB'}`,
                    background: fileDragOver ? '#FFF7ED' : '#FAFAFA',
                    fontSize:12, color: fileDragOver ? '#E36F2C' : '#6B7280',
                  }}
                >
                  {saving ? 'Uploading…' : '⬆ Drag & drop files here, or click to browse (multiple OK)'}
                  <input type="file" multiple style={{ display:'none' }} disabled={saving}
                    onChange={e => { uploadFiles(e.target.files); e.target.value=''; }} />
                </label>
              </div>
            )}

            {/* Audit history (field-level events) */}
            {section === 'history' && (
              <div style={styles.card}>
                <h2 style={{ ...styles.h2, marginBottom:10 }}>Audit Log — {reqNo(current)}</h2>
                {history.length === 0 && <div style={{ color:'#9CA3AF', fontSize:13 }}>No events recorded.</div>}
                {history.map(ev => (
                  <div key={ev.id} style={{ display:'flex', gap:10, padding:'6px 0', borderBottom:'1px solid #F3F4F6', fontSize:12 }}>
                    <span style={{ color:'#9CA3AF', whiteSpace:'nowrap' }}>
                      {new Date(ev.changed_at).toLocaleString('en-US', { dateStyle:'short', timeStyle:'short' })}
                    </span>
                    <span style={{ fontWeight:600, whiteSpace:'nowrap' }}>{ev.changed_by_name || 'system'}</span>
                    <span style={{ color:'#6B7280' }}>
                      <strong>{ev.field}</strong>
                      {ev.old_value != null && ev.old_value !== '' ? <> : {String(ev.old_value)} →</> : ' :'}
                      {' '}{ev.new_value != null ? String(ev.new_value) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {current && (
        <RevisionDrawer
          open={showRevisions}
          onClose={() => setShowRevisions(false)}
          title={`Request History — #${current.id}`}
          listFn={() => api.listRequestRevisions(current.id)}
          snapshotFn={(revId) => api.getRequestRevisionSnapshot(current.id, revId)}
          renderSnapshot={(snap) => (
            <div style={{ fontSize:12, color:'#374151' }}>
              <div><strong>Status:</strong> {snap.status}</div>
              {snap.customer_name && <div><strong>Customer:</strong> {snap.customer_name}</div>}
              <div style={{ marginTop:6, color:'#9CA3AF' }}>
                {(snap.labor_lines||[]).length} labor · {(snap.equipment_lines||[]).length} equipment · {(snap.consumable_lines||[]).length} consumables
              </div>
            </div>
          )}
        />
      )}
    </>
  );
}

// ---- Phase editor sub-component -------------------------------------------
function RequestPhaseEditor({ phases, readOnly, onSave }) {
  const byPhase = {};
  for (const ph of PHASES) {
    const found = phases.find(p => p.phase === ph);
    // pg DATE columns serialize as ISO timestamps — date inputs need YYYY-MM-DD.
    // hours_per_day: seeded rows carry NULL; the ?? 10 display default must be
    // real state or saves persist null and hours compute 0.
    byPhase[ph] = found
      ? { ...found,
          start_date: found.start_date ? String(found.start_date).slice(0, 10) : '',
          end_date: found.end_date ? String(found.end_date).slice(0, 10) : '',
          hours_per_day: found.hours_per_day ?? 10 }
      : { phase:ph, start_date:'', end_date:'', hours_per_day:10, day_hc:0, night_hc:0, work_days:'all' };
  }
  const [local, setLocal] = useState(byPhase);
  const set = (ph, field, v) => setLocal(prev => ({ ...prev, [ph]: { ...prev[ph], [field]: v } }));
  const totals = phaseHoursPreview(Object.values(local), 'weekly_over_40');

  return (
    <div>
      <div style={{ fontWeight:700, fontSize:12, marginBottom:8 }}>Phase Dates &amp; Headcount</div>
      <table style={{ fontSize:12, borderCollapse:'collapse', width:'100%' }}>
        <thead><tr style={{ borderBottom:'1px solid #E5E7EB' }}>
          <th style={{ ...styles.th, fontSize:11 }}>Phase</th>
          <th style={{ ...styles.th, fontSize:11 }}>Start</th>
          <th style={{ ...styles.th, fontSize:11 }}>End</th>
          <th style={{ ...styles.th, fontSize:11 }} title="Which weekdays count as work days — no hours book on the others">Days</th>
          <th style={{ ...styles.th, fontSize:11 }}>Hrs/Day</th>
          <th style={{ ...styles.th, fontSize:11 }}>Day HC</th>
          <th style={{ ...styles.th, fontSize:11 }}>Night HC</th>
        </tr></thead>
        <tbody>
          {PHASES.map((ph, i) => {
            const row = local[ph];
            // Phase N start opens at phase N-1's end (min on a future date
            // positions the native picker there — no month-flipping). End only
            // auto-fills via the "days" box (George 2026-06-19).
            const prevEnd = i > 0 ? (local[PHASES[i - 1]]?.end_date || '') : '';
            const badRange = row.start_date && row.end_date && row.end_date < row.start_date;
            return (
              <tr key={ph}>
                <td style={{ ...styles.td, fontWeight:600 }}>{ph}</td>
                <td style={styles.td}>
                  <input type="date" disabled={readOnly}
                    style={{ ...styles.input, fontSize:11, padding:'3px 6px' }}
                    min={prevEnd || undefined}
                    value={row.start_date || ''} onChange={e => set(ph, 'start_date', e.target.value)} />
                </td>
                <td style={styles.td}>
                  <input type="date" disabled={readOnly}
                    style={{ ...styles.input, fontSize:11, padding:'3px 6px' }}
                    min={row.start_date || undefined}
                    value={row.end_date || ''} onChange={e => set(ph, 'end_date', e.target.value)} />
                  {!readOnly && (
                    <input type="number" min="1" placeholder="days" title="Enter # of days to fill End from Start (inclusive)"
                      style={{ ...styles.input, width:46, fontSize:11, padding:'3px 4px', marginLeft:4 }}
                      value={dayCountISO(row.start_date, row.end_date) || ''}
                      onChange={e => { const end = endFromDays(row.start_date, e.target.value); if (end) set(ph, 'end_date', end); }} />
                  )}
                  {badRange && <div style={{ color:'#B91C1C', fontSize:10, fontWeight:700 }}>ends before start</div>}
                </td>
                <td style={styles.td}>
                  <select disabled={readOnly}
                    style={{ ...styles.input, width:100, fontSize:11, padding:'3px 6px' }}
                    value={row.work_days || 'all'} onChange={e => set(ph, 'work_days', e.target.value)}>
                    {WORK_DAYS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td style={styles.td}>
                  <input type="number" min="0" max="24" step="0.5" disabled={readOnly}
                    style={{ ...styles.input, width:60, fontSize:11, padding:'3px 6px' }}
                    value={row.hours_per_day} onChange={e => set(ph, 'hours_per_day', Number(e.target.value))} />
                </td>
                {['day_hc','night_hc'].map(f => (
                  <td key={f} style={styles.td}>
                    <input type="number" min="0" disabled={readOnly}
                      style={{ ...styles.input, width:50, fontSize:11, padding:'3px 6px' }}
                      value={row[f]||0} onChange={e => set(ph, f, Number(e.target.value))} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:8 }}>
        {!readOnly && (
          <button style={{ ...styles.btn, fontSize:12 }}
            onClick={() => onSave(Object.values(local))}>
            Save Phases
          </button>
        )}
        <span style={{ fontSize:12, fontWeight:700, color:'#1F2937' }}>
          Billable hours: {totals.st.toLocaleString()} ST / {totals.ot.toLocaleString()} OT
          <span style={{ fontWeight:400, color:'#9CA3AF' }}> · {totals.workDays} work days · assumes weekly &gt;40 OT</span>
        </span>
      </div>
    </div>
  );
}

// ---- MSA file attachment sub-component ------------------------------------
function MsaAttachment({ requestId, msa_filename, readOnly, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { await api.uploadRequestMsa(requestId, file); onUploaded(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const download = async () => {
    try { await api.downloadRequestMsa(requestId, msa_filename); }
    catch (e) { alert(e.message); }
  };

  const remove = async () => {
    if (!confirm('Remove the attached MSA file?')) return;
    setBusy(true);
    try { await api.deleteRequestMsa(requestId); onUploaded(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop:8, padding:'8px 12px', background:'#F0F9FF', border:'1px solid #BAE6FD', borderRadius:6 }}>
      {msa_filename ? (
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <span style={{ fontSize:12, fontWeight:600 }}>📎 {msa_filename}</span>
          <button style={{ ...styles.btnSecondary, fontSize:11, padding:'3px 10px' }} onClick={download}>
            ⬇ Download
          </button>
          {!readOnly && (
            <>
              <DropZone onFiles={files => upload(files[0])} disabled={busy} title="Drop the replacement MSA here">
                <label style={{ ...styles.btnSecondary, fontSize:11, padding:'3px 10px', cursor: busy ? 'wait' : 'pointer', display:'inline-block' }}>
                  {busy ? '…' : '↻ Replace'}
                  <input type="file" style={{ display:'none' }} disabled={busy}
                    onChange={e => { upload(e.target.files?.[0]); e.target.value=''; }} />
                </label>
              </DropZone>
              <button style={{ ...styles.btnDanger, fontSize:11, padding:'3px 8px' }} onClick={remove} disabled={busy}>
                × Remove
              </button>
            </>
          )}
        </div>
      ) : readOnly ? (
        <span style={{ fontSize:12, color:'#6B7280' }}>No MSA file attached.</span>
      ) : (
        <label
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
          style={{
            display:'block', textAlign:'center', cursor: busy ? 'wait' : 'pointer',
            padding:'12px 10px', borderRadius:6,
            border:`2px dashed ${dragOver ? '#E36F2C' : '#BAE6FD'}`,
            background: dragOver ? '#FFF7ED' : 'transparent',
            fontSize:12, color: dragOver ? '#E36F2C' : '#6B7280',
          }}
        >
          {busy ? 'Uploading…' : '⬆ Drag & drop the MSA here, or click to browse'}
          <input type="file" style={{ display:'none' }} disabled={busy}
            onChange={e => { upload(e.target.files?.[0]); e.target.value=''; }} />
        </label>
      )}
    </div>
  );
}
